import { useCallback, useEffect, useMemo, useState } from "react";
import { db, storage } from "../firebase";
import { functions } from "../firebase";
import { httpsCallable } from "firebase/functions";
import {
  arrayUnion,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  runTransaction,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import {
  deleteObject,
  getDownloadURL,
  ref,
  uploadBytes,
} from "firebase/storage";
import { createAuditEntry } from "../utils/audit";
import { displayName } from "../utils/capabilities";
import { PAYMENT_METHOD_OPTIONS } from "../models/models";
import { perVisitPriceForVisit } from "../utils/contracts";

const PAYMENT_METHOD_LABELS = Object.fromEntries(
  PAYMENT_METHOD_OPTIONS.map((o) => [o.value, o.label])
);

/**
 * Phase 3 data hook. Subscribes via onSnapshot to each of the five
 * org-scoped subcollections (customers, locations, equipment,
 * serviceRecords, contacts) and stitches them into the customer tree
 * shape the pages consume. All writes go through the actions helpers.
 *
 * The org's global audit log still lives in a single doc at
 * organizations/{orgId}/data/main (globalAuditLog field); we keep a
 * light subscription just for that.
 *
 * Technicians skip the contacts subscription — under the firestore.rules
 * they can't read that collection.
 */
export default function useOrgData(user, orgId, role, profile, orgName) {
  // Pre-computed "who is doing the write" — preferred display name with the
  // email as fallback. Snapshotted onto every new record/message so historical
  // entries stay stable even if the user updates their profile name later.
  const callerName = displayName(profile, user?.email);
  const callerOrgName = orgName || "";
  const [customersRaw, setCustomersRaw] = useState([]);
  const [locationsRaw, setLocationsRaw] = useState([]);
  const [equipmentRaw, setEquipmentRaw] = useState([]);
  const [serviceRecordsRaw, setServiceRecordsRaw] = useState([]);
  const [contactsRaw, setContactsRaw] = useState([]);
  const [jobsRaw, setJobsRaw] = useState([]);
  const [serviceTicketsRaw, setServiceTicketsRaw] = useState([]);
  const [quotesRaw, setQuotesRaw] = useState([]);
  const [purchaseOrdersRaw, setPurchaseOrdersRaw] = useState([]);
  const [contractsRaw, setContractsRaw] = useState([]);
  const [vendorsRaw, setVendorsRaw] = useState([]);
  const [timeEntriesRaw, setTimeEntriesRaw] = useState([]);
  const [facilityServiceTicketsRaw, setFacilityServiceTicketsRaw] = useState(
    []
  );
  // Contractor jobs visible to this org as a facility customer (sanitized,
  // costing stripped). Empty for contractor orgs that own the building.
  const [publicJobsRaw, setPublicJobsRaw] = useState([]);
  // Signed contractor tickets visible to this org (proof of work, no cost).
  const [publicTicketsRaw, setPublicTicketsRaw] = useState([]);
  // Customer-facing invoices from finalized contractor bills (sale prices
  // only, no cost or margin).
  const [publicInvoicesRaw, setPublicInvoicesRaw] = useState([]);
  // Customer-facing quotes (sale prices only). Drafts stay contractor-side.
  const [publicQuotesRaw, setPublicQuotesRaw] = useState([]);
  // In-app notifications targeted at this org.
  const [notificationsRaw, setNotificationsRaw] = useState([]);
  // Cross-org message threads (this org is a memberOrg).
  // messageThreadsRaw is derived (useMemo) from two listener buckets defined
  // in the dedicated effect below.

  // Global audit log still lives in data/main (globalAuditLog field).
  const [globalAuditLog, setGlobalAuditLog] = useState([]);

  const [dataLoading, setDataLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (!user || !orgId) {
      setCustomersRaw([]);
      setLocationsRaw([]);
      setEquipmentRaw([]);
      setServiceRecordsRaw([]);
      setContactsRaw([]);
      setJobsRaw([]);
      setServiceTicketsRaw([]);
      setQuotesRaw([]);
      setPurchaseOrdersRaw([]);
      setContractsRaw([]);
      setVendorsRaw([]);
      setTimeEntriesRaw([]);
      setFacilityServiceTicketsRaw([]);
      setPublicJobsRaw([]);
      setPublicTicketsRaw([]);
      setPublicInvoicesRaw([]);
      setPublicQuotesRaw([]);
      setNotificationsRaw([]);
      setGlobalAuditLog([]);
      setDataLoading(false);
      return;
    }

    setDataLoading(true);

    const firstLoad = {
      customers: false,
      locations: false,
      equipment: false,
      serviceRecords: false,
      contacts: role === "technician",
      jobs: false,
      serviceTickets: false,
      quotes: false,
      purchaseOrders: false,
      publicJobs: false,
      publicTickets: false,
      publicInvoices: false,
      publicQuotes: false,
      messageThreads: false,
      auditLog: false,
    };

    const markLoaded = (key) => {
      if (firstLoad[key]) return;
      firstLoad[key] = true;
      if (Object.values(firstLoad).every(Boolean)) {
        setDataLoading(false);
      }
    };

    const onErr = (key, setter) => (err) => {
      if (err.code === "permission-denied") {
        // Could be: (a) rules not deployed for this collection, OR
        // (b) auth token expired / refresh failed (Google 503) — the
        // rule check can't verify identity without a valid token, so
        // the listener gets permission-denied just like a missing rule.
        // Hard refresh usually fixes (b); deploy fixes (a).
        console.warn(
          `${key} unavailable (permission-denied — rules missing OR auth token refresh failed; hard refresh first) — using empty list`
        );
        setter([]);
      } else {
        console.error(`${key} listener error:`, err);
        setErrorMessage(err.message);
      }
      markLoaded(key);
    };

    // Org-scoped collections (the contractor/facility's own work records).
    const subscribeTo = (key, setter) =>
      onSnapshot(
        collection(db, `organizations/${orgId}/${key}`),
        (snap) => {
          setter(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
          markLoaded(key);
        },
        onErr(key, setter)
      );

    // Shared two-sided collections (top-level, access via memberOrgs). The
    // contractor side sees rows where its org is a member; linking a facility
    // adds their org to memberOrgs.
    const subscribeShared = (collName, key, setter) =>
      onSnapshot(
        query(
          collection(db, collName),
          where("memberOrgs", "array-contains", orgId)
        ),
        (snap) => {
          setter(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
          markLoaded(key);
        },
        onErr(key, setter)
      );

    // accounts↔customers, buildings↔locations (kept tree-shape names so pages
    // are unchanged); equipment + serviceRecords are shared too.
    const unsubCustomers = subscribeShared("accounts", "customers", setCustomersRaw);
    const unsubLocations = subscribeShared("buildings", "locations", setLocationsRaw);
    const unsubEquipment = subscribeShared("equipment", "equipment", setEquipmentRaw);
    const unsubServiceRecords = subscribeShared(
      "serviceRecords",
      "serviceRecords",
      setServiceRecordsRaw
    );
    const unsubJobs = subscribeTo("jobs", setJobsRaw);
    const unsubServiceTickets = subscribeTo(
      "serviceTickets",
      setServiceTicketsRaw
    );
    const unsubQuotes = subscribeTo("quotes", setQuotesRaw);
    const unsubPurchaseOrders = subscribeTo(
      "purchaseOrders",
      setPurchaseOrdersRaw
    );
    const unsubContracts = subscribeTo("contracts", setContractsRaw);
    const unsubVendors = subscribeTo("vendors", setVendorsRaw);
    const unsubTimeEntries = subscribeTo("timeEntries", setTimeEntriesRaw);
    const unsubFacilityTickets = subscribeTo(
      "facilityServiceTickets",
      setFacilityServiceTicketsRaw
    );
    const unsubPublicJobs = subscribeShared(
      "publicJobs",
      "publicJobs",
      setPublicJobsRaw
    );
    const unsubPublicTickets = subscribeShared(
      "publicTickets",
      "publicTickets",
      setPublicTicketsRaw
    );
    const unsubPublicInvoices = subscribeShared(
      "publicInvoices",
      "publicInvoices",
      setPublicInvoicesRaw
    );
    const unsubPublicQuotes = subscribeShared(
      "publicQuotes",
      "publicQuotes",
      setPublicQuotesRaw
    );
    // messageThreads is loaded in its own effect below (so it can self-
    // recover from the rules-deployed-after-login race, just like
    // notifications). Mark it loaded immediately here so dataLoading doesn't
    // wait on it.
    markLoaded("messageThreads");

    let unsubContacts = () => {};
    if (role !== "technician") {
      unsubContacts = subscribeTo("contacts", setContactsRaw);
    } else {
      setContactsRaw([]);
    }

    const unsubAuditLog = onSnapshot(
      doc(db, `organizations/${orgId}/data/main`),
      (snap) => {
        setGlobalAuditLog(
          snap.exists() ? snap.data().globalAuditLog || [] : []
        );
        markLoaded("auditLog");
      },
      (err) => {
        if (err.code === "permission-denied") {
          console.warn("audit log unavailable — using empty list");
        } else {
          console.error("audit log listener error:", err);
          setErrorMessage(err.message);
        }
        markLoaded("auditLog");
      }
    );

    return () => {
      unsubCustomers();
      unsubLocations();
      unsubEquipment();
      unsubServiceRecords();
      unsubJobs();
      unsubServiceTickets();
      unsubQuotes();
      unsubPurchaseOrders();
      unsubContracts();
      unsubVendors();
      unsubTimeEntries();
      unsubFacilityTickets();
      unsubPublicJobs();
      unsubPublicTickets();
      unsubPublicInvoices();
      unsubPublicQuotes();
      unsubContacts();
      unsubAuditLog();
    };
  }, [user, orgId, role]);

  // Notifications listener — kept in its own effect so it can self-recover
  // from the rules-deployed-after-login race that breaks regular onSnapshot
  // listeners (one-shot retry 3s after a permission-denied error). The bell
  // exposes `reconnectNotifications` for a manual escape hatch.
  const [notifTick, setNotifTick] = useState(0);
  const reconnectNotifications = useCallback(
    () => setNotifTick((t) => t + 1),
    []
  );
  useEffect(() => {
    if (!user || !orgId) {
      setNotificationsRaw([]);
      return;
    }
    let retried = false;
    const unsub = onSnapshot(
      query(collection(db, "notifications"), where("orgId", "==", orgId)),
      (snap) =>
        setNotificationsRaw(
          snap.docs.map((d) => ({ id: d.id, ...d.data() }))
        ),
      (err) => {
        if (err.code === "permission-denied" && !retried) {
          retried = true;
          setTimeout(() => setNotifTick((t) => t + 1), 3000);
        } else if (err.code !== "permission-denied") {
          console.error("notifications listener error:", err);
        }
      }
    );
    return unsub;
  }, [user, orgId, notifTick]);

  // Message-threads listener — two subscriptions merged into one state:
  //   1) scope != "direct": threads where my orgId is in memberOrgs
  //      (building / job / account chats with linked partners).
  //   2) scope == "direct": threads where my uid is in participants
  //      (employee-to-employee DMs).
  // Each result is keyed by id so duplicates are impossible. Same self-
  // recovering retry pattern as the notifications listener.
  const [threadsByMemberOrgs, setThreadsByMemberOrgs] = useState([]);
  const [threadsByParticipant, setThreadsByParticipant] = useState([]);
  const [threadsTick, setThreadsTick] = useState(0);
  const reconnectMessageThreads = useCallback(
    () => setThreadsTick((t) => t + 1),
    []
  );
  useEffect(() => {
    if (!user || !orgId) {
      setThreadsByMemberOrgs([]);
      setThreadsByParticipant([]);
      return;
    }
    let retried = false;
    const onListenerErr = (err) => {
      if (err.code === "permission-denied" && !retried) {
        retried = true;
        setTimeout(() => setThreadsTick((t) => t + 1), 3000);
      } else if (err.code !== "permission-denied") {
        console.error("messageThreads listener error:", err);
      }
    };
    // memberOrgs is ONLY populated on building-scope threads now (DMs and
    // future job/account threads use participants instead — see
    // startMessageThread). So this single-field array-contains query
    // returns only building threads and the rule passes for every result.
    // No composite index needed; no SDK target conflict with the
    // participants listener below.
    const unsubOrgs = onSnapshot(
      query(
        collection(db, "messageThreads"),
        where("memberOrgs", "array-contains", orgId)
      ),
      (snap) => {
        setThreadsByMemberOrgs(
          snap.docs.map((d) => ({ id: d.id, ...d.data() }))
        );
      },
      (err) => {
        console.error("[messaging] memberOrgs listener error:", err);
        onListenerErr(err);
      }
    );
    const unsubDms = user.uid
      ? onSnapshot(
          query(
            collection(db, "messageThreads"),
            where("participants", "array-contains", user.uid)
          ),
          (snap) => {
            setThreadsByParticipant(
              snap.docs.map((d) => ({ id: d.id, ...d.data() }))
            );
          },
          (err) => {
            console.error("[messaging] participants listener error:", err);
            onListenerErr(err);
          }
        )
      : () => {};
    return () => {
      unsubOrgs();
      unsubDms();
    };
  }, [user, orgId, threadsTick]);
  // Merged + deduped list.
  const messageThreadsRaw = useMemo(() => {
    const byId = new Map();
    for (const t of threadsByMemberOrgs) byId.set(t.id, t);
    for (const t of threadsByParticipant) byId.set(t.id, t);
    return [...byId.values()];
  }, [threadsByMemberOrgs, threadsByParticipant]);

  // Stitched tree from the new subcollections.
  const newTree = useMemo(() => {
    return customersRaw.map((c) => ({
      ...c,
      contacts: contactsRaw.filter(
        (contact) => contact.customerId === c.id && !contact.locationId
      ),
      locations: locationsRaw
        .filter((loc) => loc.customerId === c.id)
        .map((loc) => ({
          ...loc,
          contacts: contactsRaw.filter(
            (contact) =>
              contact.customerId === c.id && contact.locationId === loc.id
          ),
          equipment: equipmentRaw
            .filter((eq) => eq.locationId === loc.id)
            .map((eq) => ({
              ...eq,
              serviceRecords: serviceRecordsRaw.filter(
                (sr) => sr.equipmentId === eq.id
              ),
            })),
        })),
    }));
  }, [
    customersRaw,
    locationsRaw,
    equipmentRaw,
    serviceRecordsRaw,
    contactsRaw,
  ]);

  // The customer tree is sourced entirely from the subcollections now.
  const customers = newTree;

  // === Phase 3 write helpers ==============================================
  // Each helper writes directly to its subcollection.

  const orgPath = orgId ? `organizations/${orgId}` : null;

  const runWrite = async (label, op) => {
    if (!orgPath) return;
    try {
      setErrorMessage("");
      await op();
    } catch (error) {
      console.error(`${label}:`, error);
      setErrorMessage(error.message);
    }
  };

  // --- Customers / accounts (shared) ---
  const addCustomer = (customerData) =>
    runWrite("addCustomer", async () => {
      const { id, locations, contacts, ...fields } = customerData;
      await setDoc(doc(db, `accounts/${id}`), {
        ...fields,
        type: fields.type || "managed",
        ownerOrgId: orgId,
        memberOrgs: [orgId],
        createdByOrg: orgId,
      });
    });

  const updateCustomer = (id, patch) =>
    runWrite("updateCustomer", async () => {
      await updateDoc(doc(db, `accounts/${id}`), patch);
    });

  // Delete a customer (account) doc. Caller-confirmed; gated by
  // deleteCustomers capability via Firestore rules. Buildings under
  // this customer aren't auto-removed — same rationale as deleteLocation.
  const deleteCustomer = (id) =>
    runWrite("deleteCustomer", async () => {
      await deleteDoc(doc(db, `accounts/${id}`));
    });

  // --- Locations / buildings (shared) ---
  const addLocation = (customerId, locationData) =>
    runWrite("addLocation", async () => {
      const { id, equipment, contacts, ...fields } = locationData;
      // Always single-org on the primary doc. Cross-org visibility for
      // shared customers comes through publicBuildings mirrors written
      // by a Cloud Function — copying the parent's multi-org
      // memberOrgs in here would trip memberOrgsOwnedOnly() in the
      // Firestore rules and fail with "insufficient permissions".
      await setDoc(doc(db, `buildings/${id}`), {
        ...fields,
        customerId, // = accountId; kept for the existing tree stitch
        ownerOrgId: orgId,
        memberOrgs: [orgId],
        createdByOrg: orgId,
      });
    });

  const updateLocation = (id, patch) =>
    runWrite("updateLocation", async () => {
      await updateDoc(doc(db, `buildings/${id}`), patch);
    });

  // Delete a building doc. Caller is expected to have already confirmed
  // the destructive intent in the UI. Gated by deleteLocations capability
  // via Firestore rules. Cascading equipment / serviceRecords are NOT
  // auto-removed here — they remain in their collections but become
  // unreachable from the tree once the building is gone. (We can write a
  // cleanup sweep when this gets used in anger.)
  const deleteLocation = (id) =>
    runWrite("deleteLocation", async () => {
      await deleteDoc(doc(db, `buildings/${id}`));
    });

  // --- Equipment (shared) ---
  const addEquipment = (customerId, locationId, equipmentData) =>
    runWrite("addEquipment", async () => {
      const { id, serviceRecords, ...fields } = equipmentData;
      // Always single-org on primary docs — same reason as addLocation.
      await setDoc(doc(db, `equipment/${id}`), {
        ...fields,
        customerId,
        locationId, // = buildingId; kept for the existing tree stitch
        memberOrgs: [orgId],
        createdByOrg: orgId,
        createdBy: user?.email || "",
        createdByName: callerName,
        createdByOrgName: callerOrgName,
      });
    });

  const updateEquipment = (id, patch) =>
    runWrite("updateEquipment", async () => {
      await updateDoc(doc(db, `equipment/${id}`), patch);
    });

  // Hard delete. Removes the equipment doc + cascades all of its service
  // records (orphans would otherwise live in serviceRecordsRaw forever
  // since the parent equipment is gone). Embedded warranties / partsList
  // photos die with the equipment doc; Storage files are NOT cleaned up
  // here — they sit as orphans until a future storage-sweep task runs.
  const deleteEquipment = (equipmentId) =>
    runWrite("deleteEquipment", async () => {
      const batch = writeBatch(db);
      batch.delete(doc(db, `equipment/${equipmentId}`));
      serviceRecordsRaw
        .filter((r) => r.equipmentId === equipmentId)
        .forEach((r) => batch.delete(doc(db, `serviceRecords/${r.id}`)));
      await batch.commit();
    });

  // --- Service Records ---
  // Add service record + auto-bump equipment.lastPmDate when tag is
  // "Maintenance". Done in a writeBatch so both writes are atomic.
  const addServiceRecord = (customerId, locationId, equipmentId, recordData) =>
    runWrite("addServiceRecord", async () => {
      const { id, ...fields } = recordData;
      // Always single-org on primary docs — same reason as addLocation.
      const batch = writeBatch(db);
      batch.set(doc(db, `serviceRecords/${id}`), {
        ...fields,
        customerId,
        locationId,
        equipmentId,
        memberOrgs: [orgId],
        createdByOrg: orgId,
        createdBy: user?.email || "",
        createdByName: callerName,
        createdByOrgName: callerOrgName,
      });
      if (recordData.tag === "Maintenance") {
        const today = new Date().toISOString().slice(0, 10);
        batch.update(doc(db, `equipment/${equipmentId}`), {
          lastPmDate: today,
        });
      }
      await batch.commit();
    });

  const updateServiceRecord = (id, patch) =>
    runWrite("updateServiceRecord", async () => {
      await updateDoc(doc(db, `serviceRecords/${id}`), patch);
    });

  const softDeleteServiceRecord = (id, userEmail) =>
    runWrite("softDeleteServiceRecord", async () => {
      const record = serviceRecordsRaw.find((r) => r.id === id);
      await updateDoc(doc(db, `serviceRecords/${id}`), {
        deleted: true,
        deletedAt: new Date().toISOString(),
        deletedBy: userEmail || "unknown",
        auditTrail: [
          ...(record?.auditTrail || []),
          createAuditEntry(
            userEmail,
            "Deleted Service Record",
            "Service record was moved to Deleted Items"
          ),
        ],
      });
    });

  const restoreServiceRecord = (id, userEmail) =>
    runWrite("restoreServiceRecord", async () => {
      const record = serviceRecordsRaw.find((r) => r.id === id);
      await updateDoc(doc(db, `serviceRecords/${id}`), {
        deleted: false,
        deletedAt: null,
        deletedBy: null,
        restoredAt: new Date().toISOString(),
        restoredBy: userEmail || "unknown",
        auditTrail: [
          ...(record?.auditTrail || []),
          createAuditEntry(
            userEmail,
            "Restored Service Record",
            "Service record was restored from Deleted Items"
          ),
        ],
      });
    });

  const permanentlyDeleteServiceRecord = (id, userEmail) =>
    runWrite("permanentlyDeleteServiceRecord", async () => {
      const record = serviceRecordsRaw.find((r) => r.id === id);
      if (record) {
        for (const photo of record.photos || []) {
          if (photo.path) {
            await deleteObject(ref(storage, photo.path)).catch((e) =>
              console.warn("photo cleanup failed:", e.message)
            );
          }
        }
        for (const file of record.files || []) {
          if (file.path) {
            await deleteObject(ref(storage, file.path)).catch((e) =>
              console.warn("file cleanup failed:", e.message)
            );
          }
        }
      }
      await deleteDoc(doc(db, `serviceRecords/${id}`));
      await appendToAuditLog(
        "Permanently Deleted Service Record",
        `Service record ID ${id} was permanently deleted`,
        userEmail
      );
    });

  const deleteServicePhoto = (recordId, photoIndex) =>
    runWrite("deleteServicePhoto", async () => {
      const record = serviceRecordsRaw.find((r) => r.id === recordId);
      if (!record) return;
      const photo = record.photos?.[photoIndex];
      if (photo?.path) {
        await deleteObject(ref(storage, photo.path)).catch(() => {});
      }
      await updateDoc(doc(db, `serviceRecords/${recordId}`), {
        photos: (record.photos || []).filter((_, i) => i !== photoIndex),
      });
    });

  // --- Equipment parts list attachments ---
  const addPartsListAttachments = (equipmentId, newPhotos, newFiles) =>
    runWrite("addPartsListAttachments", async () => {
      const eq = equipmentRaw.find((e) => e.id === equipmentId);
      if (!eq) return;
      await updateDoc(doc(db, `equipment/${equipmentId}`), {
        partsListPhotos: [...(eq.partsListPhotos || []), ...newPhotos],
        partsListFiles: [...(eq.partsListFiles || []), ...newFiles],
      });
    });

  const deletePartsListPhoto = (equipmentId, photoIndex) =>
    runWrite("deletePartsListPhoto", async () => {
      const eq = equipmentRaw.find((e) => e.id === equipmentId);
      if (!eq) return;
      const photo = eq.partsListPhotos?.[photoIndex];
      if (photo?.path) {
        await deleteObject(ref(storage, photo.path)).catch(() => {});
      }
      await updateDoc(doc(db, `equipment/${equipmentId}`), {
        partsListPhotos: (eq.partsListPhotos || []).filter(
          (_, i) => i !== photoIndex
        ),
      });
    });

  const deletePartsListFile = (equipmentId, fileIndex) =>
    runWrite("deletePartsListFile", async () => {
      const eq = equipmentRaw.find((e) => e.id === equipmentId);
      if (!eq) return;
      const file = eq.partsListFiles?.[fileIndex];
      if (file?.path) {
        await deleteObject(ref(storage, file.path)).catch(() => {});
      }
      await updateDoc(doc(db, `equipment/${equipmentId}`), {
        partsListFiles: (eq.partsListFiles || []).filter(
          (_, i) => i !== fileIndex
        ),
      });
    });

  // --- Warranties (array field on equipment) ---
  const addEquipmentWarranty = (equipmentId, warrantyData) =>
    runWrite("addEquipmentWarranty", async () => {
      const eq = equipmentRaw.find((e) => e.id === equipmentId);
      if (!eq) return;
      const now = new Date().toISOString();
      const entry = {
        id: `w_${Date.now()}`,
        type: warrantyData.type || "",
        installDate: warrantyData.installDate || "",
        expDate: warrantyData.expDate || "",
        createdBy: user?.email || "",
        createdByName: callerName,
        createdAt: now,
        updatedBy: null,
        updatedByName: null,
        updatedAt: null,
      };
      await updateDoc(doc(db, `equipment/${equipmentId}`), {
        warranties: [...(eq.warranties || []), entry],
      });
    });

  const updateEquipmentWarranty = (equipmentId, warrantyId, patch) =>
    runWrite("updateEquipmentWarranty", async () => {
      const eq = equipmentRaw.find((e) => e.id === equipmentId);
      if (!eq) return;
      const now = new Date().toISOString();
      const next = (eq.warranties || []).map((w) =>
        w.id === warrantyId
          ? {
              ...w,
              ...patch,
              updatedBy: user?.email || "",
              updatedByName: callerName,
              updatedAt: now,
            }
          : w
      );
      await updateDoc(doc(db, `equipment/${equipmentId}`), {
        warranties: next,
      });
    });

  const deleteEquipmentWarranty = (equipmentId, warrantyId) =>
    runWrite("deleteEquipmentWarranty", async () => {
      const eq = equipmentRaw.find((e) => e.id === equipmentId);
      if (!eq) return;
      await updateDoc(doc(db, `equipment/${equipmentId}`), {
        warranties: (eq.warranties || []).filter((w) => w.id !== warrantyId),
      });
    });

  // --- Equipment status (green/yellow/red health) ---
  const updateEquipmentStatus = (equipmentId, status, note) =>
    runWrite("updateEquipmentStatus", async () => {
      const eq = equipmentRaw.find((e) => e.id === equipmentId);
      if (!eq) return;
      const patch = {
        status,
        statusNote: note || "",
        statusChangedAt: new Date().toISOString(),
        statusChangedBy: user?.email || "",
        statusChangedByName: callerName,
        statusChangedByOrg: orgId,
      };
      // Optimistic update — patch local equipmentRaw before the server
      // confirms, so the new status appears instantly everywhere (dashboard
      // counters, location equipment list, etc.) instead of waiting for
      // the Firestore listener to round-trip. If the write fails, the
      // listener will overwrite with the actual server state.
      setEquipmentRaw((prev) =>
        prev.map((e) => (e.id === equipmentId ? { ...e, ...patch } : e))
      );
      await updateDoc(doc(db, `equipment/${equipmentId}`), patch);
    });

  // --- Contacts ---
  // locationId is null for customer-level contacts, set for location-level.
  const addContact = (customerId, locationId, contactData) =>
    runWrite("addContact", async () => {
      const { id, ...fields } = contactData;
      await setDoc(doc(db, `${orgPath}/contacts/${id}`), {
        ...fields,
        customerId,
        locationId: locationId || null,
      });
    });

  const updateContact = (id, patch) =>
    runWrite("updateContact", async () => {
      await updateDoc(doc(db, `${orgPath}/contacts/${id}`), patch);
    });

  const softDeleteContact = (id, userEmail) =>
    runWrite("softDeleteContact", async () => {
      const contact = contactsRaw.find((c) => c.id === id);
      await updateDoc(doc(db, `${orgPath}/contacts/${id}`), {
        deleted: true,
        deletedAt: new Date().toISOString(),
        deletedBy: userEmail || "unknown",
        auditTrail: [
          ...(contact?.auditTrail || []),
          createAuditEntry(
            userEmail,
            "Deleted Contact",
            "Contact was moved to Deleted Items"
          ),
        ],
      });
    });

  const restoreContact = (id, userEmail) =>
    runWrite("restoreContact", async () => {
      const contact = contactsRaw.find((c) => c.id === id);
      await updateDoc(doc(db, `${orgPath}/contacts/${id}`), {
        deleted: false,
        deletedAt: null,
        deletedBy: null,
        restoredAt: new Date().toISOString(),
        restoredBy: userEmail || "unknown",
        auditTrail: [
          ...(contact?.auditTrail || []),
          createAuditEntry(
            userEmail,
            "Restored Contact",
            "Contact was restored from Deleted Items"
          ),
        ],
      });
    });

  const permanentlyDeleteContact = (id, userEmail) =>
    runWrite("permanentlyDeleteContact", async () => {
      await deleteDoc(doc(db, `${orgPath}/contacts/${id}`));
      await appendToAuditLog(
        "Permanently Deleted Contact",
        `Contact ID ${id} was permanently deleted`,
        userEmail
      );
    });

  // --- Jobs (work orders) ---
  // Auto-assigns a per-org job number YYYY#### that resets each year, using a
  // transaction on a counter doc so two simultaneous creates can't collide.
  // If the caller passes a non-blank `jobNumber` on jobData, that value is
  // used verbatim and the counter is NOT advanced — for shops that need to
  // mirror an external numbering system (customer PO, owner's internal job
  // code). Collisions with future auto numbers aren't enforced server-side
  // yet; pick custom numbers that won't look like YYYY####.
  const addJob = (jobData) =>
    runWrite("addJob", async () => {
      const { id, jobNumber: customJobNumber, ...fields } = jobData;
      const trimmedCustom = (customJobNumber || "").trim();
      if (trimmedCustom) {
        await setDoc(doc(db, `${orgPath}/jobs/${id}`), {
          ...fields,
          jobNumber: trimmedCustom,
          createdByName: fields.createdByName || callerName,
          createdByOrgName: fields.createdByOrgName || callerOrgName,
        });
        return;
      }
      const year = String(new Date().getFullYear());
      const counterRef = doc(db, `${orgPath}/counters/jobs`);
      await runTransaction(db, async (tx) => {
        const snap = await tx.get(counterRef);
        const data = snap.exists() ? snap.data() : {};
        const next = (data[year] || 0) + 1;
        const jobNumber = `${year}${String(next).padStart(4, "0")}`;
        tx.set(counterRef, { [year]: next }, { merge: true });
        tx.set(doc(db, `${orgPath}/jobs/${id}`), {
          ...fields,
          jobNumber,
          createdByName: fields.createdByName || callerName,
          createdByOrgName: fields.createdByOrgName || callerOrgName,
        });
      });
    });

  const updateJob = (id, patch) =>
    runWrite("updateJob", async () => {
      await updateDoc(doc(db, `${orgPath}/jobs/${id}`), patch);
    });

  // Flip the acknowledge flag on a publicInvoice doc. Facility-side only
  // (rules restrict the write to facility members + only the three ack
  // fields — see firestore.rules /publicInvoices). The contractor reads
  // ack status through the realtime publicInvoicesRaw subscription, so no
  // server round-trip is needed for them to see it.
  const acknowledgeInvoice = (publicInvoiceId) =>
    runWrite("acknowledgeInvoice", async () => {
      await updateDoc(doc(db, `publicInvoices/${publicInvoiceId}`), {
        acknowledged: true,
        acknowledgedAt: new Date().toISOString(),
        acknowledgedBy: user?.email || "",
      });
    });

  // Finalize a job's bill: assign a per-org INV-YYYY-#### invoice number
  // (counters/invoices, same shape as counters/jobs and counters/quotes) and
  // apply the caller's finalize patch in one transaction.
  //
  // Reopen + re-finalize REUSES the existing invoiceNumber on the job rather
  // than burning a new one — the customer's already seen INV-2026-0017, and
  // an admin tweak shouldn't shift the number under them. Phase 2 partial-
  // billing void/reissue (see project_partial_billing memory) introduces the
  // formal "this is a different invoice now" pattern.
  const finalizeBillWithInvoice = (id, patch) =>
    runWrite("finalizeBillWithInvoice", async () => {
      const jobRef = doc(db, `${orgPath}/jobs/${id}`);
      const year = String(new Date().getFullYear());
      const counterRef = doc(db, `${orgPath}/counters/invoices`);
      await runTransaction(db, async (tx) => {
        const jobSnap = await tx.get(jobRef);
        if (!jobSnap.exists()) throw new Error("Job not found");
        const existing = jobSnap.data();
        if (existing.invoiceNumber) {
          tx.update(jobRef, patch);
          return;
        }
        const counterSnap = await tx.get(counterRef);
        const counterData = counterSnap.exists() ? counterSnap.data() : {};
        const next = (counterData[year] || 0) + 1;
        const invoiceNumber = `INV-${year}-${String(next).padStart(4, "0")}`;
        tx.set(counterRef, { [year]: next }, { merge: true });
        tx.update(jobRef, { ...patch, invoiceNumber });
      });
    });

  // Generate a partial or final progress bill on a job (line-item mode).
  // Bumps counters/invoices to assign an INV-YYYY-####, stamps every
  // selected costing line's `billedOnInvoice` field, appends a new entry
  // to job.bills, and (for type === "final") closes the job.
  //
  // billData shape (caller pre-computes the math):
  //   {
  //     type: "partial" | "final",
  //     description: string,
  //     lineItemIds: string[],   // ids of labor/materials/expenses/fuel rows
  //     subtotal, taxableSubtotal,
  //     taxes: [{ name, mode, rate, amount }],
  //     grandTotal,
  //   }
  const generateBill = (jobId, billData) =>
    runWrite("generateBill", async () => {
      const jobRef = doc(db, `${orgPath}/jobs/${jobId}`);
      const year = String(new Date().getFullYear());
      const counterRef = doc(db, `${orgPath}/counters/invoices`);
      const now = new Date().toISOString();
      const billerEmail = user?.email || "unknown";
      const billerName = callerName;
      await runTransaction(db, async (tx) => {
        const jobSnap = await tx.get(jobRef);
        if (!jobSnap.exists()) throw new Error("Job not found");
        const counterSnap = await tx.get(counterRef);
        const counterData = counterSnap.exists() ? counterSnap.data() : {};
        const nextN = (counterData[year] || 0) + 1;
        const invoiceNumber = `INV-${year}-${String(nextN).padStart(4, "0")}`;
        const job = jobSnap.data();

        // Stamp billedOnInvoice on every selected line. Lines not in the
        // selection pass through unchanged so a later Progress Bill can
        // pick them up. Phase D percentage bills skip stamping entirely —
        // a percentage bill claims a slice of the contracted price, not
        // specific costing rows, so all lines stay available for P&L
        // tracking against the contract.
        const isPercentageMode = billData.billingMode === "percentage";
        const lineIdsToBill = new Set(billData.lineItemIds || []);
        const stamp = (list) =>
          (list || []).map((line) =>
            !isPercentageMode && lineIdsToBill.has(line.id)
              ? { ...line, billedOnInvoice: invoiceNumber }
              : line
          );
        const c = job.costing || {};
        const newCosting = {
          ...c,
          labor: stamp(c.labor),
          materials: stamp(c.materials),
          expenses: stamp(c.expenses),
          fuel: stamp(c.fuel),
        };

        // Default due date = finalizedAt + 30 days (Net 30). Stamped at
        // finalize time so aging buckets work out of the box without a
        // per-customer terms config. Org-billing-defaults phase will let
        // admins override this default per org / per customer.
        const dueDateObj = new Date(now);
        dueDateObj.setDate(dueDateObj.getDate() + 30);
        const dueDate = dueDateObj.toISOString().slice(0, 10);

        // Phase D: billingMode comes from billData ("line-item" or
        // "percentage"). The first non-voided bill locks the mode for
        // the rest of the job — JobCosting only shows the matching
        // Progress Bill button after that.
        const billingMode =
          billData.billingMode === "percentage" ? "percentage" : "line-item";
        const newBill = {
          id: `bill_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
          type: billData.type === "final" ? "final" : "partial",
          description: billData.description || "",
          billingMode,
          // Line-item-mode field: ids of costing rows this bill stamped.
          // Empty on percentage bills.
          lineItemIds: billingMode === "line-item"
            ? billData.lineItemIds || []
            : [],
          // Percentage-mode fields: percent + percentBase snapshot of the
          // contracted price at the moment of billing. Both empty/0 on
          // line-item bills.
          percent: billingMode === "percentage" ? billData.percent || 0 : 0,
          percentBase:
            billingMode === "percentage" ? billData.percentBase || 0 : 0,
          subtotal: billData.subtotal || 0,
          taxableSubtotal: billData.taxableSubtotal || 0,
          taxes: billData.taxes || [],
          grandTotal: billData.grandTotal || 0,
          invoiceNumber,
          finalizedAt: now,
          finalizedBy: billerEmail,
          finalizedByName: billerName,
          // Phase C: aging buckets read from dueDate. Falls back to
          // finalizedAt + 30 for pre-Phase-C bills via aging helper.
          dueDate,
          paymentStatus: "unpaid",
          paidAmount: 0,
          payments: [],
        };

        const updates = {
          bills: [...(job.bills || []), newBill],
          costing: newCosting,
          updatedAt: now,
        };
        // Final bill closes the job + sets the legacy billFinalized flags
        // so anything still keying off the old shape (existing dashboards,
        // filters, mirror functions for the single-bill path) treats this
        // as a finalized job too.
        if (newBill.type === "final") {
          updates.status = "closed";
          updates.closedAt = now;
          updates.closedBy = billerEmail;
          updates.billFinalized = true;
          updates.billFinalizedAt = now;
          updates.billFinalizedBy = billerEmail;
          updates.billFinalizedByName = billerName;
        }

        tx.set(counterRef, { [year]: nextN }, { merge: true });
        tx.update(jobRef, updates);
      });
    });

  // Accept a facility's "requested" job: assign a YYYY#### number (same
  // counter transaction as addJob) and move it to "unassigned" so it joins
  // the normal board. A requested job has no number until accepted.
  const acceptRequestedJob = (id) =>
    runWrite("acceptRequestedJob", async () => {
      const year = String(new Date().getFullYear());
      const counterRef = doc(db, `${orgPath}/counters/jobs`);
      const now = new Date().toISOString();
      await runTransaction(db, async (tx) => {
        const snap = await tx.get(counterRef);
        const data = snap.exists() ? snap.data() : {};
        const next = (data[year] || 0) + 1;
        const jobNumber = `${year}${String(next).padStart(4, "0")}`;
        tx.set(counterRef, { [year]: next }, { merge: true });
        tx.update(doc(db, `${orgPath}/jobs/${id}`), {
          jobNumber,
          status: "unassigned",
          updatedAt: now,
        });
      });
    });

  const deleteJob = (id) =>
    runWrite("deleteJob", async () => {
      await deleteDoc(doc(db, `${orgPath}/jobs/${id}`));
    });

  // --- Service tickets (work orders) ---
  const addServiceTicket = (ticketData) =>
    runWrite("addServiceTicket", async () => {
      const { id, ...fields } = ticketData;
      await setDoc(doc(db, `${orgPath}/serviceTickets/${id}`), {
        ...fields,
        createdByName: fields.createdByName || callerName,
        createdByOrgName: fields.createdByOrgName || callerOrgName,
      });
    });

  const updateServiceTicket = (id, patch) =>
    runWrite("updateServiceTicket", async () => {
      await updateDoc(doc(db, `${orgPath}/serviceTickets/${id}`), patch);
    });

  // Build the draft Estimated-Bill lines a ticket feeds (mirrors the
  // feedTicketToBill Cloud Function), tagged with the ticket id.
  const buildFedLines = (ticket) => {
    const lineId = () =>
      `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const labor = (ticket.labor || [])
      .filter((l) => l && (l.employeeName || l.hours))
      .map((l) => ({
        id: lineId(),
        description: `Ticket ${ticket.workDate || ""}: ${
          l.employeeName || ""
        }`.trim(),
        hours: l.hours || "",
        baseCost: "",
        rate: "",
        sourceTicketId: ticket.id,
      }));
    const materials = (ticket.materials || [])
      .filter((m) => m && (m.name || m.qty || m.unitCost))
      .map((m) => ({
        id: lineId(),
        name: m.name || "",
        partNumber: m.partNumber || "",
        qty: m.qty || "",
        unitCost: m.unitCost || "",
        markup: "",
        sourceTicketId: ticket.id,
      }));
    return { labor, materials };
  };

  const stripFed = (costing, ticketId) => ({
    ...(costing || {}),
    labor: (costing?.labor || []).filter((l) => l.sourceTicketId !== ticketId),
    materials: (costing?.materials || []).filter(
      (m) => m.sourceTicketId !== ticketId
    ),
  });

  // Delete a ticket AND remove the bill lines it fed, atomically. Admin/
  // manager only (matches the rules: ticket delete + job.costing write).
  const deleteServiceTicket = (id) =>
    runWrite("deleteServiceTicket", async () => {
      const ticket = serviceTicketsRaw.find((t) => t.id === id);
      const batch = writeBatch(db);
      batch.delete(doc(db, `${orgPath}/serviceTickets/${id}`));
      const job = ticket && jobsRaw.find((j) => j.id === ticket.jobId);
      if (job?.costing) {
        batch.update(doc(db, `${orgPath}/jobs/${job.id}`), {
          costing: stripFed(job.costing, id),
          updatedAt: new Date().toISOString(),
        });
      }
      await batch.commit();
    });

  // Hard-delete a SIGNED ticket. The local deleteServiceTicket above
  // can't handle this — signed tickets are locked at the rules layer +
  // they've already produced timeEntries via feedTicketTimeToBoard that
  // need to be reversed too. So we route through a Cloud Function that
  // does the full cascade server-side under admin SDK + capability gate
  // (deleteSignedTickets). See functions/index.js for details.
  const deleteSignedTicket = (id) =>
    runWrite("deleteSignedTicket", async () => {
      const fn = httpsCallable(functions, "deleteSignedServiceTicket");
      const res = await fn({ ticketId: id });
      return res.data;
    });

  // Move a ticket to another job: re-point the ticket and migrate its fed
  // bill lines from the old job to the new one, atomically. Equipment
  // selection is cleared (it's location-specific). Admin/manager only.
  const moveTicket = (id, targetJobId) =>
    runWrite("moveTicket", async () => {
      const ticket = serviceTicketsRaw.find((t) => t.id === id);
      const target = jobsRaw.find((j) => j.id === targetJobId);
      if (!ticket || !target || ticket.jobId === targetJobId) return;
      const oldJob = jobsRaw.find((j) => j.id === ticket.jobId);
      const now = new Date().toISOString();
      const batch = writeBatch(db);

      batch.update(doc(db, `${orgPath}/serviceTickets/${id}`), {
        jobId: targetJobId,
        customerId: target.customerId || "",
        locationId: target.locationId || "",
        equipmentIds: [],
        updatedAt: now,
      });

      if (oldJob?.costing) {
        batch.update(doc(db, `${orgPath}/jobs/${oldJob.id}`), {
          costing: stripFed(oldJob.costing, id),
          updatedAt: now,
        });
      }

      const fed = buildFedLines(ticket);
      const nc = stripFed(target.costing, id);
      batch.update(doc(db, `${orgPath}/jobs/${targetJobId}`), {
        costing: {
          ...nc,
          labor: [...nc.labor, ...fed.labor],
          materials: [...nc.materials, ...fed.materials],
        },
        updatedAt: now,
      });

      await batch.commit();
    });

  // --- Quotes ---
  // Auto-assigns a per-org quote number Q-YYYY#### (resets each year) via a
  // transaction on counters/quotes, same pattern as job numbers.
  const addQuote = (quoteData) =>
    runWrite("addQuote", async () => {
      const { id, ...fields } = quoteData;
      const year = String(new Date().getFullYear());
      const counterRef = doc(db, `${orgPath}/counters/quotes`);
      await runTransaction(db, async (tx) => {
        const snap = await tx.get(counterRef);
        const data = snap.exists() ? snap.data() : {};
        const next = (data[year] || 0) + 1;
        const quoteNumber = `Q-${year}${String(next).padStart(4, "0")}`;
        tx.set(counterRef, { [year]: next }, { merge: true });
        tx.set(doc(db, `${orgPath}/quotes/${id}`), {
          ...fields,
          quoteNumber,
          createdByName: fields.createdByName || callerName,
          createdByOrgName: fields.createdByOrgName || callerOrgName,
        });
      });
    });

  const updateQuote = (id, patch) =>
    runWrite("updateQuote", async () => {
      await updateDoc(doc(db, `${orgPath}/quotes/${id}`), patch);
    });

  // Amend a SENT quote -- snapshots the current state into the
  // revisionHistory[] array, bumps the revision counter, and flips
  // status back to "draft" so admin can reopen the editor without
  // losing the prior version. Quote number stays the same across
  // revisions; customer-facing PDFs render "Rev N" badges so the
  // customer can tell what they're looking at.
  //
  // Pre-conditions enforced UI-side (button only shows when met):
  //   - quote.status === "sent" (approved/declined skip this path)
  //   - caller has createQuotes capability
  //
  // Reset on amend:
  //   - sentAt cleared (next Send stamps a fresh date)
  //   - contractorSignature cleared (must be re-captured -- amended
  //     quote is a NEW authorization, not the original)
  //   - status -> draft (editor reopens)
  // Stamped on amend:
  //   - revision += 1
  //   - amendedAt / amendedBy / amendedByName
  const amendQuote = (id) =>
    runWrite("amendQuote", async () => {
      const current = quotesRaw.find((q) => q.id === id);
      if (!current) throw new Error("Quote not found");
      if (current.status !== "sent") {
        throw new Error(
          `Cannot amend a quote in status "${current.status}". Only sent quotes can be amended.`
        );
      }
      // Snapshot is everything ABOUT the prior state -- except the
      // revisionHistory itself (don't recurse / explode the doc size).
      const { revisionHistory: _prev, ...snapshot } = current;
      const now = new Date().toISOString();
      const newRevision = (current.revision || 0) + 1;
      await updateDoc(doc(db, `${orgPath}/quotes/${id}`), {
        revision: newRevision,
        revisionHistory: [...(current.revisionHistory || []), snapshot],
        status: "draft",
        sentAt: null,
        contractorSignature: null,
        amendedAt: now,
        amendedBy: user?.email || "",
        amendedByName: callerName,
        updatedAt: now,
      });
    });

  const deleteQuote = (id) =>
    runWrite("deleteQuote", async () => {
      await deleteDoc(doc(db, `${orgPath}/quotes/${id}`));
    });

  // Customer-facing total of a quote — sums line items + taxes the same way
  // QuotesPage/quoteTotal does. Snapshotted onto the new job's costing at
  // approval time so the bill's customer-facing total stays locked at the
  // quoted price even if techs add labor/materials later (P&L variance
  // shows up against actuals).
  const computeQuoteTotal = (q) => {
    const n = (v) => {
      const x = parseFloat(v);
      return Number.isFinite(x) ? x : 0;
    };
    const c = q.costing || {};
    const withMarkup = (l) => n(l.qty) * n(l.cost) * (1 + n(l.markup) / 100);
    // Fuel base = miles 1-way × 2 × trips × $/mile, OR flat amount.
    // Markup % applied either way.
    const fuelBase = (f) =>
      f.mode === "flat"
        ? n(f.amount)
        : n(f.costPerMile) * n(f.milesOneWay) * 2 * n(f.trips);
    const fuelTotal = (f) => fuelBase(f) * (1 + n(f.markup) / 100);
    const sub =
      (c.labor || []).reduce((s, l) => s + n(l.hours) * n(l.price), 0) +
      (c.materials || []).reduce((s, m) => s + withMarkup(m), 0) +
      (c.charges || []).reduce((s, x) => s + withMarkup(x), 0) +
      (c.fuel || []).reduce((s, f) => s + fuelTotal(f), 0);
    const tax = (c.taxes || []).reduce(
      (s, t) => s + (t.mode === "flat" ? n(t.amount) : sub * (n(t.rate) / 100)),
      0
    );
    return sub + tax;
  };

  // Mark a quote approved AND create the linked job (numbered) seeded from the
  // quote's pricing — atomically. Admin/manager (they hold createJobs).
  const approveQuoteAndCreateJob = (quoteId) =>
    runWrite("approveQuoteAndCreateJob", async () => {
      const quote = quotesRaw.find((q) => q.id === quoteId);
      if (!quote) return;
      const year = String(new Date().getFullYear());
      const jobCounterRef = doc(db, `${orgPath}/counters/jobs`);
      const now = new Date().toISOString();
      const quotedAmount = computeQuoteTotal(quote);
      await runTransaction(db, async (tx) => {
        const snap = await tx.get(jobCounterRef);
        const data = snap.exists() ? snap.data() : {};
        const next = (data[year] || 0) + 1;
        const jobNumber = `${year}${String(next).padStart(4, "0")}`;
        const jobRef = doc(collection(db, `${orgPath}/jobs`));
        tx.set(jobCounterRef, { [year]: next }, { merge: true });
        tx.set(jobRef, {
          jobNumber,
          title: quote.title || `Quote ${quote.quoteNumber || ""}`,
          description: quote.scope || "",
          jobType: "Quoted",
          customerId: quote.customerId || "",
          locationId: quote.locationId || "",
          equipmentIds: quote.equipmentIds || [],
          updateUponDiagnostic: false,
          status: "unassigned",
          assignedTechs: [],
          createdAt: now,
          createdBy: user?.email || "unknown",
          createdByName: callerName,
          updatedAt: now,
          closedAt: null,
          closedBy: null,
          // Quoted-job bills start empty — labor/materials/expenses
          // accumulate from tickets + POs as work is done. The
          // customer pays the locked quoted price regardless; the
          // line items below it exist only for internal P&L. Seeding
          // from the quote's lines would double-count anything a tech
          // then adds via a ticket.
          costing: {
            labor: [],
            materials: [],
            expenses: [],
            fuel: [],
            taxes: [],
            billDescription: quote.scope || "",
            priceMode: "quoted",
            quotedAmount,
          },
          quoteId: quote.id,
          quoteNumber: quote.quoteNumber || "",
        });
        tx.update(doc(db, `${orgPath}/quotes/${quoteId}`), {
          status: "approved",
          approvedAt: now,
          jobId: jobRef.id,
          updatedAt: now,
        });
      });
    });

  // Maintenance-quote sibling of approveQuoteAndCreateJob.
  // Approves the quote AND creates a Contract from it — same visit-spawn
  // logic addContract uses (counters/contracts + counters/jobs both
  // bumped atomically; one job per scheduled visit). The line-item
  // subtotal becomes the contract's annualPrice; contract-specific
  // fields ride over from the quote (term years, start date, visit
  // interval, billing mode/interval). Renewal: if the quote was a
  // renewal (renewalOfContractId set), the old contract gets renewedTo
  // and the new contract gets renewedFrom in the same transaction.
  const approveQuoteAndCreateContract = (quoteId) =>
    runWrite("approveQuoteAndCreateContract", async () => {
      const quote = quotesRaw.find((q) => q.id === quoteId);
      if (!quote) return;
      if (quote.quoteType !== "maintenance") return; // wrong pipeline
      const year = String(new Date().getFullYear());
      const now = new Date().toISOString();
      // Annual price = line-item subtotal + taxes. The customer signs for
      // this number every year of the term; per-visit math is annual /
      // visitsPerYear when each visit's bill is generated.
      const annualPrice = computeQuoteTotal(quote);
      const visitsPerYear = visitsPerYearFor(quote.contractVisitInterval);
      const termYears = parseInt(quote.contractTermYears, 10) || 1;
      const totalVisits = visitsPerYear * termYears;
      const monthsPerVisit = 12 / visitsPerYear;
      // Per-visit price — customer-facing total for ONE visit. Stamped on
      // every spawned visit job as quotedAmount so JobCosting locks the
      // customer total at this number (line items accumulate underneath for
      // internal P&L; variance shown vs. quoted). Mirrors the one-time
      // approveQuoteAndCreateJob priceMode dance.
      //
      // Year-N visits pick up the contract's annualEscalationPct when set
      // (defaults to 0 = flat pricing). perVisitContext below is the shape
      // perVisitPriceForVisit expects.
      const escalationPct = Number(quote.contractAnnualEscalationPct) || 0;
      const perVisitContext = {
        annualPrice,
        visitInterval: quote.contractVisitInterval || "quarterly",
        annualEscalationPct: escalationPct,
      };
      const startDate =
        quote.contractStartDate || new Date().toISOString().slice(0, 10);

      // Contract end date = startDate + termYears, minus one day. Same
      // shape as addContract so display + status flips stay consistent.
      const endDate = (() => {
        const [y2, m2, d2] = (startDate || "")
          .split("-")
          .map((s) => parseInt(s, 10));
        if (!y2 || !m2 || !d2) return "";
        const dt = new Date(y2 + termYears, m2 - 1, d2);
        dt.setDate(dt.getDate() - 1);
        const yy = dt.getFullYear();
        const mm = String(dt.getMonth() + 1).padStart(2, "0");
        const dd = String(dt.getDate()).padStart(2, "0");
        return `${yy}-${mm}-${dd}`;
      })();

      // Pre-compute visit doc IDs + scheduled dates so the transaction
      // body only does writes — same pattern as addContract.
      const contractRef = doc(collection(db, `${orgPath}/contracts`));
      const visitDocs = [];
      for (let i = 0; i < totalVisits; i++) {
        const scheduledDate = addMonthsToDate(
          startDate,
          Math.round(monthsPerVisit * i)
        );
        visitDocs.push({
          id: doc(collection(db, `${orgPath}/jobs`)).id,
          visitIndex: i + 1,
          scheduledDate,
        });
      }

      const contractCounterRef = doc(db, `${orgPath}/counters/contracts`);
      const jobCounterRef = doc(db, `${orgPath}/counters/jobs`);
      const quoteRef = doc(db, `${orgPath}/quotes/${quoteId}`);
      const priorContractRef = quote.renewalOfContractId
        ? doc(db, `${orgPath}/contracts/${quote.renewalOfContractId}`)
        : null;

      await runTransaction(db, async (tx) => {
        const contractCounterSnap = await tx.get(contractCounterRef);
        const contractCounter = contractCounterSnap.exists()
          ? contractCounterSnap.data()
          : {};
        const nextContractN = (contractCounter[year] || 0) + 1;
        const contractNumber = `C-${year}-${String(nextContractN).padStart(
          4,
          "0"
        )}`;

        const jobCounterSnap = await tx.get(jobCounterRef);
        const jobCounter = jobCounterSnap.exists() ? jobCounterSnap.data() : {};
        const startJobN = jobCounter[year] || 0;

        tx.set(contractRef, {
          contractNumber,
          title: quote.title || `Maintenance — ${quote.quoteNumber || ""}`,
          customerId: quote.customerId || "",
          locationId: quote.locationId || "",
          type: "PM",
          startDate,
          termYears,
          endDate,
          annualPrice,
          billingMode: quote.contractBillingMode || "per-visit",
          billingInterval: quote.contractBillingInterval || "quarterly",
          visitInterval: quote.contractVisitInterval || "quarterly",
          annualEscalationPct: escalationPct,
          defaultTechId: quote.contractDefaultTechId || "",
          scopeOfWork: quote.scope || "",
          equipmentIds: quote.equipmentIds || [],
          status: "active",
          // Renewal pointers (one direction now; the prior contract gets
          // its renewedTo set below in the same transaction).
          renewedFrom: quote.renewalOfContractId || null,
          renewedTo: null,
          // Quote linkage — useful for audit + jumping back to the signed
          // quote from the contract folder.
          sourceQuoteId: quote.id,
          sourceQuoteNumber: quote.quoteNumber || "",
          createdAt: now,
          createdBy: user?.email || "",
          createdByName: callerName,
          cancelledAt: null,
          cancelledBy: null,
          cancelReason: null,
        });

        // Spawn visit jobs — same shape addContract uses.
        visitDocs.forEach((v, i) => {
          const jobNumber = `${year}${String(startJobN + i + 1).padStart(
            4,
            "0"
          )}`;
          const visitTitle = `${
            quote.title || "PM Visit"
          } — Visit ${v.visitIndex} of ${totalVisits}`;
          tx.set(doc(db, `${orgPath}/jobs/${v.id}`), {
            jobNumber,
            title: visitTitle,
            description: quote.scope || "",
            jobType: "Maintenance",
            customerId: quote.customerId || "",
            locationId: quote.locationId || "",
            equipmentIds: quote.equipmentIds || [],
            otherEquipment: false,
            updateUponDiagnostic: false,
            status: "scheduled",
            // Pre-QB runway #7: pre-assign the contract's default tech to
            // every spawned visit. Empty array when defaultTechId is unset.
            assignedTechs: quote.contractDefaultTechId
              ? [quote.contractDefaultTechId]
              : [],
            bills: [],
            createdAt: now,
            createdBy: user?.email || "",
            createdByName: callerName,
            createdByOrgName: callerOrgName,
            updatedAt: now,
            closedAt: null,
            closedBy: null,
            guestLabor: [],
            // priceMode=quoted locks the bill's customer total at
            // quotedAmount (the per-visit price). Techs file tickets, labor
            // and materials accumulate into costing line items below, but
            // the customer pays quotedAmount; the gap is the visit's P&L.
            // Same mechanism one-time quoted jobs use.
            costing: {
              labor: [],
              materials: [],
              expenses: [],
              fuel: [],
              taxes: [],
              billDescription: quote.scope || "",
              priceMode: "quoted",
              quotedAmount: perVisitPriceForVisit(perVisitContext, v.visitIndex),
            },
            contractId: contractRef.id,
            visitIndex: v.visitIndex,
            scheduledDate: v.scheduledDate,
            isMaintenanceVisit: true,
            visitAuditTrail: [],
            skipReason: "",
          });
        });

        // Mark the source quote approved + link the new contract.
        tx.update(quoteRef, {
          status: "approved",
          approvedAt: now,
          contractId: contractRef.id,
          updatedAt: now,
        });

        // Close the renewal loop if applicable.
        if (priorContractRef) {
          tx.update(priorContractRef, {
            renewedTo: contractRef.id,
            // Status flip happens on natural expiry — don't force it here.
          });
        }

        tx.set(
          contractCounterRef,
          { [year]: nextContractN },
          { merge: true }
        );
        tx.set(
          jobCounterRef,
          { [year]: startJobN + totalVisits },
          { merge: true }
        );
      });
    });

  // --- Purchase orders ---
  // Plain create — used for tech "requests" (status "requested", no number).
  const addPurchaseOrder = (poData) =>
    runWrite("addPurchaseOrder", async () => {
      const { id, ...fields } = poData;
      await setDoc(doc(db, `${orgPath}/purchaseOrders/${id}`), {
        ...fields,
        createdByName: fields.createdByName || callerName,
        createdByOrgName: fields.createdByOrgName || callerOrgName,
      });
    });

  // Authorized create — assigns a PO-YYYY#### number in a transaction.
  const addPurchaseOrderNumbered = (poData) =>
    runWrite("addPurchaseOrderNumbered", async () => {
      const { id, ...fields } = poData;
      const year = String(new Date().getFullYear());
      const counterRef = doc(db, `${orgPath}/counters/purchaseOrders`);
      await runTransaction(db, async (tx) => {
        const snap = await tx.get(counterRef);
        const data = snap.exists() ? snap.data() : {};
        const next = (data[year] || 0) + 1;
        const poNumber = `PO-${year.slice(2)}-${String(next).padStart(4, "0")}`;
        tx.set(counterRef, { [year]: next }, { merge: true });
        tx.set(doc(db, `${orgPath}/purchaseOrders/${id}`), {
          ...fields,
          poNumber,
          createdByName: fields.createdByName || callerName,
          createdByOrgName: fields.createdByOrgName || callerOrgName,
        });
      });
    });

  const updatePurchaseOrder = (id, patch) =>
    runWrite("updatePurchaseOrder", async () => {
      await updateDoc(doc(db, `${orgPath}/purchaseOrders/${id}`), patch);
    });

  const deletePurchaseOrder = (id) =>
    runWrite("deletePurchaseOrder", async () => {
      await deleteDoc(doc(db, `${orgPath}/purchaseOrders/${id}`));
    });

  // --- Maintenance Contracts (Chunk 4) ---

  // Compute how many visits a given interval gives per year. Used both to
  // size the contract's visit array and to derive per-visit price.
  const visitsPerYearFor = (interval) =>
    ({ monthly: 12, quarterly: 4, biannual: 2, annual: 1 }[interval] || 4);

  // Add `months` whole months to a YYYY-MM-DD date, returning YYYY-MM-DD.
  // Handles end-of-month rollover by clamping to the last day of the
  // target month (e.g. Jan 31 + 1mo -> Feb 28).
  const addMonthsToDate = (yyyyMmDd, months) => {
    const [y, m, d] = yyyyMmDd.split("-").map((s) => parseInt(s, 10));
    const dt = new Date(y, (m - 1) + months, 1);
    const daysInTargetMonth = new Date(
      dt.getFullYear(),
      dt.getMonth() + 1,
      0
    ).getDate();
    dt.setDate(Math.min(d, daysInTargetMonth));
    const yy = dt.getFullYear();
    const mm = String(dt.getMonth() + 1).padStart(2, "0");
    const dd = String(dt.getDate()).padStart(2, "0");
    return `${yy}-${mm}-${dd}`;
  };

  // Add a maintenance contract + spawn all N visits in one transaction.
  // The contract gets a per-org C-YYYY-#### number (counters/contracts,
  // same shape as counters/quotes and counters/jobs); each visit gets a
  // YYYY#### job number from counters/jobs and lives in /jobs with
  // contractId + isMaintenanceVisit + visitIndex + scheduledDate +
  // status "scheduled" so it doesn't clutter the active JobsPage tiles
  // until admin activates it from the contract folder.
  //
  // contractData is the shape from createContract() — needs at minimum
  // customerId, locationId, startDate, termYears, annualPrice, visitInterval,
  // billingMode, billingInterval, title.
  const addContract = (contractData) =>
    runWrite("addContract", async () => {
      const { id: contractId, ...fields } = contractData;
      const year = String(new Date().getFullYear());
      const contractCounterRef = doc(
        db,
        `${orgPath}/counters/contracts`
      );
      const jobCounterRef = doc(db, `${orgPath}/counters/jobs`);
      const visitsPerYear = visitsPerYearFor(fields.visitInterval);
      const totalVisits = visitsPerYear * (fields.termYears || 1);
      const monthsPerVisit = 12 / visitsPerYear;
      // Per-visit price stamped on every visit job's costing.quotedAmount —
      // makes JobCosting treat each visit like a quoted job (customer total
      // locked, line items accumulate underneath for internal P&L). Same
      // dance as approveQuoteAndCreateContract; admin-set annualPrice from
      // the ContractsPage form is the source of truth here. Each visit's
      // stamped price reflects its year-of-term so the annualEscalationPct
      // (if any) lands correctly when the visit eventually auto-drafts.
      const perVisitContext = {
        annualPrice: parseFloat(fields.annualPrice) || 0,
        visitInterval: fields.visitInterval,
        annualEscalationPct: Number(fields.annualEscalationPct) || 0,
      };
      // Compute the contract end date (last day before start + term years)
      // — used purely for display + status flips later.
      const endDate = (() => {
        const [y2, m2, d2] = (fields.startDate || "")
          .split("-")
          .map((s) => parseInt(s, 10));
        if (!y2 || !m2 || !d2) return "";
        const dt = new Date(y2 + (fields.termYears || 1), m2 - 1, d2);
        dt.setDate(dt.getDate() - 1);
        const yy = dt.getFullYear();
        const mm = String(dt.getMonth() + 1).padStart(2, "0");
        const dd = String(dt.getDate()).padStart(2, "0");
        return `${yy}-${mm}-${dd}`;
      })();

      const now = new Date().toISOString();
      const visitDocs = [];
      for (let i = 0; i < totalVisits; i++) {
        const scheduledDate = addMonthsToDate(
          fields.startDate || now.slice(0, 10),
          Math.round(monthsPerVisit * i)
        );
        visitDocs.push({
          id: doc(collection(db, `${orgPath}/jobs`)).id,
          visitIndex: i + 1,
          scheduledDate,
        });
      }

      await runTransaction(db, async (tx) => {
        // Increment both counters.
        const contractCounterSnap = await tx.get(contractCounterRef);
        const contractCounter = contractCounterSnap.exists()
          ? contractCounterSnap.data()
          : {};
        const nextContractN = (contractCounter[year] || 0) + 1;
        const contractNumber = `C-${year}-${String(nextContractN).padStart(
          4,
          "0"
        )}`;

        const jobCounterSnap = await tx.get(jobCounterRef);
        const jobCounter = jobCounterSnap.exists() ? jobCounterSnap.data() : {};
        const startJobN = jobCounter[year] || 0;

        // Build + write the contract doc.
        const contractRef = doc(db, `${orgPath}/contracts/${contractId}`);
        tx.set(contractRef, {
          ...fields,
          contractNumber,
          endDate,
          status: "active",
          createdAt: now,
          createdBy: user?.email || "",
          createdByName: callerName,
        });

        // Spawn visit jobs. Each one gets a sequential YYYY#### number off
        // the same counter regular jobs use — visits ARE jobs.
        visitDocs.forEach((v, i) => {
          const jobNumber = `${year}${String(startJobN + i + 1).padStart(
            4,
            "0"
          )}`;
          const visitTitle = `${fields.title || "PM Visit"} — Visit ${
            v.visitIndex
          } of ${totalVisits}`;
          tx.set(doc(db, `${orgPath}/jobs/${v.id}`), {
            jobNumber,
            title: visitTitle,
            description: fields.scopeOfWork || "",
            jobType: "Maintenance",
            customerId: fields.customerId || "",
            locationId: fields.locationId || "",
            equipmentIds: fields.equipmentIds || [],
            otherEquipment: false,
            updateUponDiagnostic: false,
            // "scheduled" keeps the visit OFF the active JobsPage tiles
            // until admin activates it from the contract's timeline.
            status: "scheduled",
            // Pre-QB runway #7: pre-assign the contract's default tech.
            assignedTechs: fields.defaultTechId ? [fields.defaultTechId] : [],
            bills: [],
            createdAt: now,
            createdBy: user?.email || "",
            createdByName: callerName,
            createdByOrgName: callerOrgName,
            updatedAt: now,
            closedAt: null,
            closedBy: null,
            guestLabor: [],
            // priceMode=quoted locks each visit's customer total at
            // quotedAmount. Tech labor + materials accumulate in the
            // costing arrays below for P&L tracking; the customer pays
            // the locked per-visit price regardless. Mirrors the one-time
            // approveQuoteAndCreateJob seed.
            costing: {
              labor: [],
              materials: [],
              expenses: [],
              fuel: [],
              taxes: [],
              billDescription: fields.scopeOfWork || "",
              priceMode: "quoted",
              quotedAmount: perVisitPriceForVisit(perVisitContext, v.visitIndex),
            },
            // Maintenance-visit metadata.
            contractId,
            visitIndex: v.visitIndex,
            scheduledDate: v.scheduledDate,
            isMaintenanceVisit: true,
            visitAuditTrail: [],
            skipReason: "",
          });
        });

        // Stamp both counters last.
        tx.set(
          contractCounterRef,
          { [year]: nextContractN },
          { merge: true }
        );
        tx.set(
          jobCounterRef,
          { [year]: startJobN + totalVisits },
          { merge: true }
        );
      });
    });

  // Edit a contract doc — title / scope / equipment / status etc. Doesn't
  // touch the existing visit jobs (they're independent docs); use the
  // visit-level actions below to reschedule / skip / activate.
  const updateContract = (id, patch) =>
    runWrite("updateContract", async () => {
      await updateDoc(doc(db, `${orgPath}/contracts/${id}`), patch);
    });

  // Close a maintenance visit AND -- if the effective bill automation
  // is "auto-draft" on a per-visit contract -- pre-add one charge line
  // at the contracted per-visit price so the office can review +
  // finalize without retyping the amount. Two writes in one atomic
  // batch so the visit can't end up "closed with no draft" or vice
  // versa.
  //
  // billAutomationEffective is computed by the caller (JobDetailPage)
  // since it has the org default + contract override + maintenance
  // contract objects already in scope.
  const completeMaintenanceVisit = (
    jobId,
    { autoDraft, contract, visitJob }
  ) =>
    runWrite("completeMaintenanceVisit", async () => {
      const now = new Date().toISOString();
      const closerEmail = user?.email || "unknown";
      const jobRef = doc(db, `${orgPath}/jobs/${jobId}`);
      const patch = {
        status: "closed",
        updatedAt: now,
        closedAt: now,
        closedBy: closerEmail,
      };

      if (autoDraft && contract && visitJob) {
        // Per-visit price respects the contract's annualEscalationPct so
        // year-2+ visits auto-draft at the bumped rate. Falls back to
        // flat pricing when escalator is 0/unset.
        const perVisit = perVisitPriceForVisit(
          contract,
          visitJob.visitIndex || 1
        );
        const totalVisits =
          (visitsPerYearFor(contract.visitInterval) || 1) *
          (Number(contract.termYears) || 1);
        const visitIdx = visitJob.visitIndex || 1;
        const newLine = {
          id: `mv_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
          description: `Maintenance Visit ${visitIdx} of ${totalVisits} — contracted`,
          qty: "1",
          cost: perVisit.toFixed(2),
          markup: "0",
          taxable: true,
          autoDrafted: true,
        };
        const existing = visitJob.costing || {};
        patch.costing = {
          ...existing,
          charges: [...(existing.charges || []), newLine],
        };
      }

      await updateDoc(jobRef, patch);
    });

  // Cancel a contract — flips status + records reason. Doesn't auto-cancel
  // the spawned visit jobs; admin cancels future visits explicitly from
  // the contract folder (Phase 2 work). Single-tech shops would
  // generally cancel the remaining "scheduled" visits manually.
  const cancelContract = (id, reason) =>
    runWrite("cancelContract", async () => {
      await updateDoc(doc(db, `${orgPath}/contracts/${id}`), {
        status: "cancelled",
        cancelledAt: new Date().toISOString(),
        cancelledBy: user?.email || "",
        cancelReason: reason || "",
      });
    });

  // Reschedule a single visit — visit's scheduledDate moves to a new
  // date, status stays scheduled. Audit trail captures old/new date +
  // who/when. Subsequent visits do NOT auto-slide.
  const rescheduleVisit = (jobId, newDate, note) =>
    runWrite("rescheduleVisit", async () => {
      const ref = doc(db, `${orgPath}/jobs/${jobId}`);
      const snap = await getDoc(ref);
      if (!snap.exists()) return;
      const job = snap.data();
      const entry = {
        type: "reschedule",
        from: job.scheduledDate || "",
        to: newDate,
        by: user?.email || "",
        byName: callerName,
        at: new Date().toISOString(),
        note: note || "",
      };
      await updateDoc(ref, {
        scheduledDate: newDate,
        visitAuditTrail: [...(job.visitAuditTrail || []), entry],
        updatedAt: new Date().toISOString(),
      });
    });

  // Skip a visit — status flips to "skipped", reason captured. In per-visit
  // billing mode (Justin's shop default) skipped visits don't generate a
  // bill. Audit-trailed.
  const skipVisit = (jobId, reason) =>
    runWrite("skipVisit", async () => {
      const ref = doc(db, `${orgPath}/jobs/${jobId}`);
      const snap = await getDoc(ref);
      if (!snap.exists()) return;
      const job = snap.data();
      const now = new Date().toISOString();
      const entry = {
        type: "skip",
        by: user?.email || "",
        byName: callerName,
        at: now,
        reason: reason || "",
      };
      await updateDoc(ref, {
        status: "skipped",
        skipReason: reason || "",
        visitAuditTrail: [...(job.visitAuditTrail || []), entry],
        updatedAt: now,
      });
    });

  // Activate a scheduled visit into the regular Job lifecycle. Status
  // flips to "assigned" if the contract's default tech (or another
  // dispatcher's pick) is already on assignedTechs; otherwise
  // "unassigned" and it lands in the dispatcher's queue.
  const activateVisit = (jobId) =>
    runWrite("activateVisit", async () => {
      const job = jobsRaw.find((j) => j.id === jobId);
      const hasTech = (job?.assignedTechs || []).length > 0;
      await updateDoc(doc(db, `${orgPath}/jobs/${jobId}`), {
        status: hasTech ? "assigned" : "unassigned",
        updatedAt: new Date().toISOString(),
      });
    });

  // Issue a PO to the vendor: assign a PO-YYYY#### number (transaction on
  // counters/purchaseOrders) + status "ordered". Needs createPOs.
  const markPOOrdered = (id, expectedArrival, auditTrail) =>
    runWrite("markPOOrdered", async () => {
      const year = String(new Date().getFullYear());
      const counterRef = doc(db, `${orgPath}/counters/purchaseOrders`);
      const now = new Date().toISOString();
      await runTransaction(db, async (tx) => {
        const snap = await tx.get(counterRef);
        const data = snap.exists() ? snap.data() : {};
        const next = (data[year] || 0) + 1;
        const poNumber = `PO-${year.slice(2)}-${String(next).padStart(4, "0")}`;
        tx.set(counterRef, { [year]: next }, { merge: true });
        const patch = {
          poNumber,
          status: "ordered",
          orderedAt: now,
          expectedArrival: expectedArrival || "",
          updatedAt: now,
        };
        if (auditTrail) patch.auditTrail = auditTrail;
        tx.update(doc(db, `${orgPath}/purchaseOrders/${id}`), patch);
      });
    });

  // --- Message threads ---
  // Start a new thread. Caller picks the scope (building/job/account) and
  // the entity it's scoped to; we stamp memberOrgs from that entity so the
  // other linked org sees the thread immediately.
  const generateThreadId = () =>
    `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

  const startMessageThread = ({
    scope,
    scopeId,
    scopeLabel,
    memberOrgs: memberOrgsArg,
    subject,
    body,
    // For scope === "direct" only:
    participants, // [uid1, uid2]
    participantNames, // [name1, name2] — denormalized for listing display
    participantEmails, // [email1, email2]
  }) =>
    runWrite("startMessageThread", async () => {
      if (!body?.trim()) return;
      const isDirect = scope === "direct";
      if (!isDirect && (!memberOrgsArg || memberOrgsArg.length < 2)) return;
      if (
        isDirect &&
        (!participants || participants.length !== 2 ||
          !participants.every(Boolean))
      ) {
        return;
      }
      const threadId = generateThreadId();
      const messageId = generateThreadId();
      const now = new Date().toISOString();
      const email = user?.email || "unknown";
      // Two sequential writes (NOT a batch): the message-create rule does a
      // get() on the parent thread to verify membership, and Firestore rules
      // get() reads the COMMITTED state — pending writes from the same batch
      // aren't visible to it. So the thread must exist before its first
      // message can be written.
      const threadDoc = isDirect
        ? {
            scope: "direct",
            participants,
            participantNames: participantNames || [],
            participantEmails: participantEmails || [],
            // DMs do NOT set memberOrgs — they're gated by participants only.
            // Setting memberOrgs would make them appear in the by-memberOrgs
            // query and fail the rule (since non-participants in the same
            // org shouldn't see DMs), which crashes the listener.
            memberOrgs: [],
            subject: subject || "",
            createdByOrg: orgId,
            createdByUid: user?.uid || "",
            createdByEmail: email,
            createdByName: callerName,
            createdAt: now,
            lastMessageAt: now,
            lastMessageBody: body.trim().slice(0, 200),
            lastMessageByOrg: orgId,
            lastMessageByUid: user?.uid || "",
            lastMessageByEmail: email,
            lastMessageByName: callerName,
          }
        : {
            memberOrgs: memberOrgsArg,
            scope,
            scopeId: scopeId || null,
            scopeLabel: scopeLabel || "",
            subject: subject || "",
            createdByOrg: orgId,
            createdByUid: user?.uid || "",
            createdByEmail: email,
            createdByName: callerName,
            createdAt: now,
            lastMessageAt: now,
            lastMessageBody: body.trim().slice(0, 200),
            lastMessageByOrg: orgId,
            lastMessageByUid: user?.uid || "",
            lastMessageByEmail: email,
            lastMessageByName: callerName,
          };
      try {
        await setDoc(doc(db, `messageThreads/${threadId}`), threadDoc);
        await setDoc(
          doc(db, `messageThreads/${threadId}/messages/${messageId}`),
          {
            senderOrgId: orgId,
            senderUid: user?.uid || "",
            senderEmail: email,
            senderName: callerName,
            body: body.trim(),
            createdAt: now,
          }
        );
      } catch (err) {
        console.error("[messaging] startMessageThread write FAILED:", err);
        throw err;
      }
      return threadId;
    });

  // Stamp the user's "last read" timestamp for this thread. Used by the
  // inbox + dashboard counter to figure out per-user unread state.
  const markThreadRead = (threadId) =>
    runWrite("markThreadRead", async () => {
      if (!user?.uid || !threadId) return;
      await updateDoc(doc(db, `users/${user.uid}`), {
        [`threadLastRead.${threadId}`]: new Date().toISOString(),
      });
    });

  // Wipe a thread and every message under it. Firestore doesn't cascade-
  // delete subcollections, so we fetch the messages, then commit one batch
  // that drops them PLUS the parent thread (rules read the still-existing
  // thread, so this passes). Admin/manager only per the rules.
  const deleteMessageThread = (threadId) =>
    runWrite("deleteMessageThread", async () => {
      const msgsSnap = await getDocs(
        collection(db, `messageThreads/${threadId}/messages`)
      );
      const batch = writeBatch(db);
      msgsSnap.forEach((d) => batch.delete(d.ref));
      batch.delete(doc(db, `messageThreads/${threadId}`));
      await batch.commit();
    });

  const sendMessage = (threadId, body) =>
    runWrite("sendMessage", async () => {
      if (!body?.trim()) return;
      const messageId = generateThreadId();
      const now = new Date().toISOString();
      const email = user?.email || "unknown";
      const batch = writeBatch(db);
      batch.set(
        doc(db, `messageThreads/${threadId}/messages/${messageId}`),
        {
          senderOrgId: orgId,
          senderUid: user?.uid || "",
          senderEmail: email,
          senderName: callerName,
          body: body.trim(),
          createdAt: now,
        }
      );
      batch.update(doc(db, `messageThreads/${threadId}`), {
        lastMessageAt: now,
        lastMessageBody: body.trim().slice(0, 200),
        lastMessageByOrg: orgId,
        lastMessageByUid: user?.uid || "",
        lastMessageByEmail: email,
        lastMessageByName: callerName,
      });
      await batch.commit();
    });

  // --- Notifications ---
  const markNotificationRead = (id) =>
    runWrite("markNotificationRead", async () => {
      await updateDoc(doc(db, `notifications/${id}`), {
        read: true,
        readAt: new Date().toISOString(),
      });
    });

  // Per-user dismiss. Notifications themselves are org-wide; we add their
  // ids to the caller's dismissedNotificationIds so they vanish from THIS
  // user's bell without removing them from anyone else's.
  const dismissNotifications = (ids) =>
    runWrite("dismissNotifications", async () => {
      if (!user?.uid || !ids?.length) return;
      await updateDoc(doc(db, `users/${user.uid}`), {
        dismissedNotificationIds: arrayUnion(...ids),
      });
    });

  // --- Audit log ---
  // Lives in its own field of data/main. Written with merge so it never
  // disturbs anything else in that doc.
  const appendToAuditLog = async (action, details, userEmail) => {
    if (!orgPath) return;
    const entry = createAuditEntry(userEmail, action, details);
    const updatedLog = [entry, ...globalAuditLog];
    try {
      await setDoc(
        doc(db, `${orgPath}/data/main`),
        { globalAuditLog: updatedLog },
        { merge: true }
      );
      setGlobalAuditLog(updatedLog);
    } catch (error) {
      console.error("appendToAuditLog:", error);
      setErrorMessage(error.message);
    }
  };

  // === Time tracking (org-scoped subcollection) ===========================
  // Source: "clock" (live in/out) or "manual" (after-the-fact entry).
  // Status: "pending" -> "approved" -> "locked". Edits allowed by tech only
  // while pending; admin (approveTime) can edit/unlock anytime.
  const computeDuration = (startIso, endIso) => {
    if (!startIso || !endIso) return 0;
    const ms = new Date(endIso).getTime() - new Date(startIso).getTime();
    return Math.max(0, Math.round(ms / 60000));
  };

  const startTimeClock = (jobId, note) =>
    runWrite("startTimeClock", async () => {
      if (!orgPath || !user?.uid) return;
      const now = new Date().toISOString();
      // Auto-stop any other live clock owned by this user.
      const open = (timeEntriesRaw || []).filter(
        (e) => e.userId === user.uid && e.source === "clock" && !e.endAt
      );
      const batch = writeBatch(db);
      for (const e of open) {
        batch.update(doc(db, `${orgPath}/timeEntries/${e.id}`), {
          endAt: now,
          durationMinutes: computeDuration(e.startAt, now),
        });
      }
      const newRef = doc(collection(db, `${orgPath}/timeEntries`));
      batch.set(newRef, {
        userId: user.uid,
        userEmail: user.email || "",
        userName: callerName,
        jobId: jobId || null,
        source: "clock",
        startAt: now,
        endAt: null,
        durationMinutes: 0,
        payType: "straight",
        notes: (note || "").trim(),
        status: "pending",
        createdAt: now,
        createdBy: user.email || "",
        approvedAt: null,
        approvedBy: null,
        approvedByName: null,
        lockedAt: null,
      });
      await batch.commit();
    });

  const stopTimeClock = () =>
    runWrite("stopTimeClock", async () => {
      if (!orgPath || !user?.uid) return;
      const open = (timeEntriesRaw || []).find(
        (e) => e.userId === user.uid && e.source === "clock" && !e.endAt
      );
      if (!open) return;
      const now = new Date().toISOString();
      await updateDoc(doc(db, `${orgPath}/timeEntries/${open.id}`), {
        endAt: now,
        durationMinutes: computeDuration(open.startAt, now),
      });
    });

  const addManualTimeEntry = ({ jobId, startAt, endAt, notes, payType }) =>
    runWrite("addManualTimeEntry", async () => {
      if (!orgPath || !user?.uid) return;
      const now = new Date().toISOString();
      const newRef = doc(collection(db, `${orgPath}/timeEntries`));
      await setDoc(newRef, {
        userId: user.uid,
        userEmail: user.email || "",
        userName: callerName,
        jobId: jobId || null,
        source: "manual",
        startAt: startAt || null,
        endAt: endAt || null,
        durationMinutes: computeDuration(startAt, endAt),
        payType: payType || "straight",
        notes: (notes || "").trim(),
        status: "pending",
        createdAt: now,
        createdBy: user.email || "",
        approvedAt: null,
        approvedBy: null,
        approvedByName: null,
        lockedAt: null,
      });
    });

  // Upload (or replace) the org's logo. Stored under
  // organizations/{orgId}/logo/{timestamp}-{filename} so the previous file
  // is left as an orphan we clean up by tracking logoStoragePath.
  const uploadOrgLogo = (file) =>
    runWrite("uploadOrgLogo", async () => {
      if (!orgId || !file) return;
      const safeName = (file.name || "logo").replace(/[^a-z0-9._-]/gi, "_");
      const path = `organizations/${orgId}/logo/${Date.now()}-${safeName}`;
      const logoRef = ref(storage, path);
      await uploadBytes(logoRef, file);
      const url = await getDownloadURL(logoRef);
      // Look up the old path (if any) so we can clean it up after the
      // org-doc write succeeds.
      let oldPath = null;
      try {
        const snap = await getDoc(doc(db, `organizations/${orgId}`));
        oldPath = snap.exists() ? snap.data().logoStoragePath || null : null;
      } catch (e) {
        // Reads against the org doc require membership; if it fails, just
        // skip the cleanup — the orphan file is harmless.
      }
      await updateDoc(doc(db, `organizations/${orgId}`), {
        logoUrl: url,
        logoStoragePath: path,
      });
      if (oldPath && oldPath !== path) {
        await deleteObject(ref(storage, oldPath)).catch(() => {});
      }
    });

  const removeOrgLogo = () =>
    runWrite("removeOrgLogo", async () => {
      if (!orgId) return;
      let oldPath = null;
      try {
        const snap = await getDoc(doc(db, `organizations/${orgId}`));
        oldPath = snap.exists() ? snap.data().logoStoragePath || null : null;
      } catch (e) {}
      await updateDoc(doc(db, `organizations/${orgId}`), {
        logoUrl: "",
        logoStoragePath: "",
      });
      if (oldPath) {
        await deleteObject(ref(storage, oldPath)).catch(() => {});
      }
    });

  // Update the org-level pay-types list. Defaults seed when org has none:
  // Straight 1.0x, Over Time 1.5x, Double Time 2.0x. Admin/finance can add
  // more (Holiday, Travel, Premium, etc.) — anything goes as long as each
  // entry has { id, name, multiplier }. id stays stable; rename is fine.
  const setOrgPayTypes = (payTypes) =>
    runWrite("setOrgPayTypes", async () => {
      if (!orgId) return;
      await updateDoc(doc(db, `organizations/${orgId}`), {
        payTypes: Array.isArray(payTypes) ? payTypes : [],
      });
    });

  // Org-wide quote/bill defaults. Pre-fills new quotes (terms, validDays,
  // footer on PDFs) and pre-fills new line items in QuoteEditor +
  // JobCosting (labor cost/charge rate, material + other-charge markup).
  // Editable per quote -- this is just seed data. Save passes through a
  // shape clean so unknown fields don't slip into the org doc.
  const setOrgQuoteDefaults = (defaults) =>
    runWrite("setOrgQuoteDefaults", async () => {
      if (!orgId) return;
      const num = (v) => {
        const n = parseFloat(v);
        return Number.isFinite(n) ? n : 0;
      };
      const clean = {
        terms: String(defaults?.terms || ""),
        validDays: Math.max(1, Math.floor(num(defaults?.validDays) || 30)),
        footer: String(defaults?.footer || ""),
        laborCost: num(defaults?.laborCost),
        laborCharge: num(defaults?.laborCharge),
        materialMarkup: num(defaults?.materialMarkup),
        chargeMarkup: num(defaults?.chargeMarkup),
      };
      await updateDoc(doc(db, `organizations/${orgId}`), {
        quoteDefaults: clean,
      });
    });

  // Org-wide maintenance billing defaults. Drives the auto-draft
  // behavior when a per-visit contract's visit gets closed: a single
  // pre-filled charge line is added to the visit's costing so office
  // can review + finalize without retyping the contracted price.
  // Per-contract override available via contract.billAutomation.
  const setOrgMaintenanceDefaults = (defaults) =>
    runWrite("setOrgMaintenanceDefaults", async () => {
      if (!orgId) return;
      const billAutomation =
        defaults?.billAutomation === "auto-draft" ? "auto-draft" : "off";
      await updateDoc(doc(db, `organizations/${orgId}`), {
        maintenanceDefaults: { billAutomation },
      });
    });

  // Org-wide pay period configuration. Drives the "Current pay period"
  // filter on PayrollPage + the Group-by-Week start day. Shape:
  //   { frequency: "weekly" | "biweekly" | "monthly",
  //     startDayOfWeek: "monday" | "sunday",
  //     anchorDate: "YYYY-MM-DD"  (biweekly only -- cycle reference) }
  // Semi-monthly intentionally not supported (Justin 2026-06-15: almost
  // no commercial mechanical shops use it).
  const setOrgPayPeriodConfig = (config) =>
    runWrite("setOrgPayPeriodConfig", async () => {
      if (!orgId) return;
      const clean = {
        frequency: config?.frequency || "weekly",
        startDayOfWeek: config?.startDayOfWeek || "monday",
        anchorDate: config?.anchorDate || "",
      };
      await updateDoc(doc(db, `organizations/${orgId}`), {
        payPeriodConfig: clean,
      });
    });

  // Save the org's mailing address + main phone — shown on every printed
  // bill / quote / invoice / signed ticket so the recipient knows who
  // sent it. Pass a flat object: { street, city, state, zip, phone }.
  const setOrgAddress = (addr = {}) =>
    runWrite("setOrgAddress", async () => {
      if (!orgId) return;
      await updateDoc(doc(db, `organizations/${orgId}`), {
        address: {
          street: (addr.street || "").trim(),
          city: (addr.city || "").trim(),
          state: (addr.state || "").trim(),
          zip: (addr.zip || "").trim(),
          phone: (addr.phone || "").trim(),
        },
      });
    });

  const updateTimeEntry = (id, patch) =>
    runWrite("updateTimeEntry", async () => {
      if (!orgPath) return;
      const next = { ...patch };
      if (
        Object.prototype.hasOwnProperty.call(patch, "startAt") ||
        Object.prototype.hasOwnProperty.call(patch, "endAt")
      ) {
        const entry = timeEntriesRaw.find((e) => e.id === id);
        const startAt = patch.startAt ?? entry?.startAt;
        const endAt = patch.endAt ?? entry?.endAt;
        next.durationMinutes = computeDuration(startAt, endAt);
      }
      await updateDoc(doc(db, `${orgPath}/timeEntries/${id}`), next);
    });

  const approveTimeEntry = (id) =>
    runWrite("approveTimeEntry", async () => {
      if (!orgPath) return;
      await updateDoc(doc(db, `${orgPath}/timeEntries/${id}`), {
        status: "approved",
        approvedAt: new Date().toISOString(),
        approvedBy: user?.email || "",
        approvedByName: callerName,
      });
    });

  const unapproveTimeEntry = (id) =>
    runWrite("unapproveTimeEntry", async () => {
      if (!orgPath) return;
      await updateDoc(doc(db, `${orgPath}/timeEntries/${id}`), {
        status: "pending",
        approvedAt: null,
        approvedBy: null,
        approvedByName: null,
      });
    });

  const lockTimeEntry = (id) =>
    runWrite("lockTimeEntry", async () => {
      if (!orgPath) return;
      await updateDoc(doc(db, `${orgPath}/timeEntries/${id}`), {
        status: "locked",
        lockedAt: new Date().toISOString(),
      });
    });

  const unlockTimeEntry = (id) =>
    runWrite("unlockTimeEntry", async () => {
      if (!orgPath) return;
      await updateDoc(doc(db, `${orgPath}/timeEntries/${id}`), {
        status: "approved",
        lockedAt: null,
      });
    });

  const deleteTimeEntry = (id) =>
    runWrite("deleteTimeEntry", async () => {
      if (!orgPath) return;
      await deleteDoc(doc(db, `${orgPath}/timeEntries/${id}`));
    });

  // --- Facility-side service tickets (org-private, lighter than the
  // contractor version — time + materials + photos only, no costing).
  const addFacilityServiceTicket = (ticketData) =>
    runWrite("addFacilityServiceTicket", async () => {
      if (!orgPath) return;
      const { id, ...fields } = ticketData;
      await setDoc(doc(db, `${orgPath}/facilityServiceTickets/${id}`), {
        ...fields,
        createdBy: user?.email || "",
        createdByName: callerName,
      });
    });

  const updateFacilityServiceTicket = (id, patch) =>
    runWrite("updateFacilityServiceTicket", async () => {
      if (!orgPath) return;
      await updateDoc(
        doc(db, `${orgPath}/facilityServiceTickets/${id}`),
        patch
      );
    });

  const deleteFacilityServiceTicket = (id) =>
    runWrite("deleteFacilityServiceTicket", async () => {
      if (!orgPath) return;
      const t = facilityServiceTicketsRaw.find((x) => x.id === id);
      // Best-effort photo cleanup.
      for (const p of t?.photos || []) {
        if (p?.path) {
          await deleteObject(ref(storage, p.path)).catch(() => {});
        }
      }
      await deleteDoc(doc(db, `${orgPath}/facilityServiceTickets/${id}`));
    });

  // Record a customer payment against an issued bill (Phase B AR). Appends
  // an entry to bill.payments[] (audit trail of every cheque / ACH /
  // installment) + recomputes paidAmount + paymentStatus from the running
  // sum. Partial payments supported — paidAmount tracks the running total
  // independent of grandTotal. Voided bills cannot receive payments.
  const recordBillPayment = (jobId, billId, payment) =>
    runWrite("recordBillPayment", async () => {
      const job = jobsRaw.find((j) => j.id === jobId);
      if (!job) return;
      const now = new Date().toISOString();
      const amount = parseFloat(payment.amount) || 0;
      if (amount <= 0) return;
      const entry = {
        id: `pay_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
        amount,
        method: payment.method || "other",
        date: payment.date || now.slice(0, 10),
        note: payment.note || "",
        paidBy: user?.email || "",
        paidByName: callerName,
        recordedAt: now,
      };

      // Two shapes share this action so the bookkeeper has ONE workflow
      // from the invoices list regardless of which path the bill took:
      //   (a) Multi-bill progress-bill flow (billId set) — payment lands on
      //       the matching entry in job.bills[].
      //   (b) Legacy single-finalize flow (billId null/undefined) — payment
      //       lands on root-level job fields. These predate Phase B so the
      //       fields are seeded on first payment.
      let publicInvoiceId = null;
      if (billId) {
        const bill = (job.bills || []).find((b) => b.id === billId);
        if (!bill || bill.voided) return;
        const updatedPayments = [...(bill.payments || []), entry];
        const paidAmount = updatedPayments.reduce(
          (s, p) => s + (parseFloat(p.amount) || 0),
          0
        );
        const grand = parseFloat(bill.grandTotal) || 0;
        const paymentStatus =
          paidAmount + 0.005 >= grand
            ? "paid"
            : paidAmount > 0
            ? "partial"
            : "unpaid";
        const updatedBills = (job.bills || []).map((b) =>
          b.id === billId
            ? {
                ...b,
                payments: updatedPayments,
                paidAmount,
                paymentStatus,
                lastPaymentAt: now,
              }
            : b
        );
        await updateDoc(doc(db, `${orgPath}/jobs/${jobId}`), {
          bills: updatedBills,
          updatedAt: now,
        });
        publicInvoiceId = `${jobId}_${billId}`;
      } else {
        // Legacy single-finalize path.
        if (!job.billFinalized) return;
        const updatedPayments = [...(job.payments || []), entry];
        const paidAmount = updatedPayments.reduce(
          (s, p) => s + (parseFloat(p.amount) || 0),
          0
        );
        const grand = parseFloat(job.billGrandTotal || job.quotedAmount || 0);
        const paymentStatus =
          grand > 0 && paidAmount + 0.005 >= grand
            ? "paid"
            : paidAmount > 0
            ? "partial"
            : "unpaid";
        await updateDoc(doc(db, `${orgPath}/jobs/${jobId}`), {
          payments: updatedPayments,
          paidAmount,
          paymentStatus,
          lastPaymentAt: now,
          updatedAt: now,
        });
        publicInvoiceId = jobId;
      }

      // Auto-acknowledge on payment. Justin's call: recording payment
      // proves the customer saw the invoice, so the public mirror's ack
      // flag flips here instead of waiting on the facility user to click
      // Acknowledge themselves. Wrapped in try/catch because the mirror
      // doc may not exist yet for very fresh bills (Cloud Function mirror
      // runs async) — we don't want a missing mirror to block the payment.
      if (publicInvoiceId) {
        try {
          await updateDoc(doc(db, `publicInvoices/${publicInvoiceId}`), {
            acknowledged: true,
            acknowledgedAt: now,
            acknowledgedBy: user?.email || "",
          });
        } catch (e) {
          // Mirror may not exist yet or rules may block — log + continue.
          // The payment itself is already persisted.
          console.warn(
            "auto-ack on payment failed (non-fatal):",
            e?.message || e
          );
        }
      }
    });

  // Approve a verified vendor bill for payment (Phase B AP). Flips a flag
  // on po.vendorBill — the bookkeeper signs off that this is OK to pay.
  // Separate from actually recording payment so two-person workflows can
  // split "approve" and "cut check" between people.
  const approveVendorBillForPayment = (poId) =>
    runWrite("approveVendorBillForPayment", async () => {
      const po = purchaseOrdersRaw.find((p) => p.id === poId);
      if (!po || !po.vendorBill?.verifiedAt) return;
      const now = new Date().toISOString();
      await updateDoc(doc(db, `${orgPath}/purchaseOrders/${poId}`), {
        vendorBill: {
          ...po.vendorBill,
          approvedForPayment: true,
          approvedAt: now,
          approvedBy: user?.email || "",
          approvedByName: callerName,
        },
        auditTrail: [
          ...(po.auditTrail || []),
          createAuditEntry(
            user?.email,
            "Approved Vendor Bill for Payment",
            `Bill ${po.vendorBill.invoiceNumber || "(no #)"} cleared for payment`
          ),
        ],
        updatedAt: now,
      });
    });

  // Record a payment against a vendor bill (Phase B AP). Appends to
  // vendorBill.payments[] + recomputes paidAmount + paymentStatus. Same
  // shape as recordBillPayment for symmetry — billing clerk runs the
  // same workflow on both sides.
  const recordVendorBillPayment = (poId, payment) =>
    runWrite("recordVendorBillPayment", async () => {
      const po = purchaseOrdersRaw.find((p) => p.id === poId);
      if (!po || !po.vendorBill?.verifiedAt) return;
      const now = new Date().toISOString();
      const amount = parseFloat(payment.amount) || 0;
      if (amount <= 0) return;
      const entry = {
        id: `pay_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
        amount,
        method: payment.method || "other",
        date: payment.date || now.slice(0, 10),
        note: payment.note || "",
        paidBy: user?.email || "",
        paidByName: callerName,
        recordedAt: now,
      };
      const updatedPayments = [...(po.vendorBill.payments || []), entry];
      const paidAmount = updatedPayments.reduce(
        (s, p) => s + (parseFloat(p.amount) || 0),
        0
      );
      const total = parseFloat(po.vendorBill.totalAmount) || 0;
      const paymentStatus =
        paidAmount + 0.005 >= total
          ? "paid"
          : paidAmount > 0
          ? "partial"
          : "unpaid";
      await updateDoc(doc(db, `${orgPath}/purchaseOrders/${poId}`), {
        vendorBill: {
          ...po.vendorBill,
          payments: updatedPayments,
          paidAmount,
          paymentStatus,
          lastPaymentAt: now,
        },
        auditTrail: [
          ...(po.auditTrail || []),
          createAuditEntry(
            user?.email,
            "Recorded Vendor Payment",
            `${
              PAYMENT_METHOD_LABELS[entry.method] || entry.method
            } ${entry.amount.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            })}${entry.note ? ` — ${entry.note}` : ""}`
          ),
        ],
        updatedAt: now,
      });
    });

  // Void an issued bill (Phase A AR). Stamps voided + voidReason on the
  // bill subobject + clears billedOnInvoice from costing lines that
  // referenced this bill's INV# so they're available for re-billing.
  // INV# is NOT reused (real-world standard: the doc stays with its
  // number, just marked voided). When the voided bill was the FINAL bill,
  // the job reopens — admin can re-bill via a fresh Progress Bill.
  const voidBill = (jobId, billId, voidReason, voidNote) =>
    runWrite("voidBill", async () => {
      const job = jobsRaw.find((j) => j.id === jobId);
      if (!job) return;
      const bill = (job.bills || []).find((b) => b.id === billId);
      if (!bill || bill.voided) return;
      const now = new Date().toISOString();
      const invoiceNumber = bill.invoiceNumber;

      const updatedBills = (job.bills || []).map((b) =>
        b.id === billId
          ? {
              ...b,
              voided: true,
              voidedAt: now,
              voidedBy: user?.email || "",
              voidedByName: callerName,
              voidReason: voidReason || "",
              voidNote: voidNote || "",
            }
          : b
      );

      // Free up the costing lines that were bound to this invoice — they
      // can ride on the next Progress Bill. Other bills' lines untouched.
      const c = job.costing || {};
      const clearStamp = (list) =>
        (list || []).map((line) =>
          line.billedOnInvoice === invoiceNumber
            ? { ...line, billedOnInvoice: null }
            : line
        );
      const newCosting = {
        ...c,
        labor: clearStamp(c.labor),
        materials: clearStamp(c.materials),
        expenses: clearStamp(c.expenses),
        fuel: clearStamp(c.fuel),
      };

      const updates = {
        bills: updatedBills,
        costing: newCosting,
        updatedAt: now,
      };

      // Voiding a final bill reopens the job so it can be re-billed.
      // Legacy billFinalized flags + status get reverted.
      if (bill.type === "final") {
        updates.status = "in_progress";
        updates.closedAt = null;
        updates.closedBy = null;
        updates.billFinalized = false;
        updates.billFinalizedAt = null;
        updates.billFinalizedBy = null;
        updates.billFinalizedByName = null;
      }

      await updateDoc(doc(db, `${orgPath}/jobs/${jobId}`), updates);
    });

  // Verify a vendor bill against a received PO (Phase A2). Writes the
  // vendorBill subobject + flips status to "bill_verified" + appends an
  // audit entry. Variance fields are computed by the caller (UI already
  // shows variance for the verifier to review); we just persist them.
  const verifyVendorBill = (poId, bill) =>
    runWrite("verifyVendorBill", async () => {
      const po = purchaseOrdersRaw.find((p) => p.id === poId);
      if (!po) return;
      const now = new Date().toISOString();
      const summary = `Vendor bill ${
        bill.invoiceNumber || "(no #)"
      } verified — total ${(bill.totalAmount || 0).toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
      })}${
        bill.varianceAmount
          ? `, variance ${
              bill.varianceAmount > 0 ? "+" : ""
            }${bill.varianceAmount.toFixed(2)} (${bill.varianceReason || "no reason"})`
          : " — matched PO"
      }`;
      // Phase C: stamp dueDate at verification time so AP aging buckets
      // work. Default Net 30 from the bill's receivedDate (when the bill
      // physically landed at office). Vendor.paymentTerms-driven dueDate
      // calc lands in the org-billing-defaults phase.
      const baseDate = bill.receivedDate || now.slice(0, 10);
      let dueDate = "";
      const [yy, mm, dd] = baseDate.split("-").map((s) => parseInt(s, 10));
      if (yy && mm && dd) {
        const dt = new Date(yy, mm - 1, dd);
        dt.setDate(dt.getDate() + 30);
        const y2 = dt.getFullYear();
        const m2 = String(dt.getMonth() + 1).padStart(2, "0");
        const d2 = String(dt.getDate()).padStart(2, "0");
        dueDate = `${y2}-${m2}-${d2}`;
      }
      await updateDoc(doc(db, `${orgPath}/purchaseOrders/${poId}`), {
        status: "bill_verified",
        vendorBill: {
          ...bill,
          dueDate,
          paymentStatus: "unpaid",
          paidAmount: 0,
          payments: [],
          verifiedAt: now,
          verifiedBy: user?.email || "",
          verifiedByName: callerName,
        },
        auditTrail: [
          ...(po.auditTrail || []),
          createAuditEntry(user?.email, "Verified Vendor Bill", summary),
        ],
        updatedAt: now,
      });
    });

  // --- Vendors (Phase A0) ---
  // Per-org vendor master at organizations/{orgId}/vendors/{vendorId}.
  // PO creation looks vendors up by id; po.vendorName denormalized at
  // write time so vendor renames don't rewrite PO history. Gated by
  // manageVendors capability (default-on in Owner + Billing presets).
  const addVendor = (vendorData) =>
    runWrite("addVendor", async () => {
      const { id, ...fields } = vendorData;
      await setDoc(doc(db, `${orgPath}/vendors/${id}`), {
        ...fields,
        // Fallback displayName so dropdowns always have something
        // human to render even if admin left it blank.
        displayName: fields.displayName || fields.name || "",
        createdAt: fields.createdAt || new Date().toISOString(),
        createdBy: user?.email || "",
        createdByName: callerName,
      });
    });

  const updateVendor = (id, patch) =>
    runWrite("updateVendor", async () => {
      await updateDoc(doc(db, `${orgPath}/vendors/${id}`), {
        ...patch,
        updatedAt: new Date().toISOString(),
        updatedBy: user?.email || "",
        updatedByName: callerName,
      });
    });

  // Hard delete — Phase 1. POs that already reference this vendor keep
  // their denormalized po.vendorName so history is preserved; the
  // vendor just drops out of the create-PO dropdown. Phase 2 might add
  // a "block delete when active POs exist" guard.
  const deleteVendor = (id) =>
    runWrite("deleteVendor", async () => {
      await deleteDoc(doc(db, `${orgPath}/vendors/${id}`));
    });

  // One-time turnover wipe — nukes the contractor's OPERATIONAL data
  // (contracts, jobs incl. their bills/invoices, quotes, purchase
  // orders, vendors) plus the per-collection counters so newly-issued
  // numbers reset to #0001. PRESERVES customers / locations / equipment
  // (the service tree), service tickets, users, org config. Also clears
  // the cross-org public mirrors this contractor owns so facility-side
  // pages don't show ghosts.
  //
  // Admin-only invocation (UI gates by isOrgAdmin); callers should
  // multi-confirm before invoking. This is destructive and irreversible.
  const wipeOperationalData = () =>
    runWrite("wipeOperationalData", async () => {
      const deleteAllIn = async (path) => {
        const snap = await getDocs(collection(db, path));
        const docs = snap.docs;
        for (let i = 0; i < docs.length; i += 400) {
          const batch = writeBatch(db);
          for (const d of docs.slice(i, i + 400)) {
            batch.delete(d.ref);
          }
          await batch.commit();
        }
        return docs.length;
      };
      // Org-scoped operational collections.
      await deleteAllIn(`${orgPath}/contracts`);
      await deleteAllIn(`${orgPath}/jobs`);
      await deleteAllIn(`${orgPath}/quotes`);
      await deleteAllIn(`${orgPath}/purchaseOrders`);
      await deleteAllIn(`${orgPath}/vendors`);
      // Reset counters so first newly-created contract is C-YYYY-0001,
      // first newly-created job is YYYY0001, etc.
      const counterIds = ["jobs", "invoices", "contracts", "quotes", "pos"];
      for (const id of counterIds) {
        try {
          await deleteDoc(doc(db, `${orgPath}/counters/${id}`));
        } catch (_) {
          // Counter doc may not exist if no docs of that type were ever
          // created — ignore.
        }
      }
      // Public mirrors that this contractor owns. Cloud Functions
      // SHOULD cascade delete from the primary writes above, but
      // belt-and-suspenders — explicitly delete here so the facility
      // side doesn't keep showing phantom rows if the trigger lags
      // or fails.
      const mirrorCollections = [
        "publicJobs",
        "publicInvoices",
        "publicQuotes",
        "publicContracts",
      ];
      for (const coll of mirrorCollections) {
        try {
          const q = query(
            collection(db, coll),
            where("contractorOrgId", "==", orgId)
          );
          const snap = await getDocs(q);
          for (let i = 0; i < snap.docs.length; i += 400) {
            const batch = writeBatch(db);
            for (const d of snap.docs.slice(i, i + 400)) {
              batch.delete(d.ref);
            }
            await batch.commit();
          }
        } catch (e) {
          // Mirror collection may not exist or rules may block; log and
          // continue so the operational wipe still completes.
          console.warn(`Mirror wipe for ${coll} failed:`, e?.message || e);
        }
      }
    });

  const actions = {
    addCustomer,
    updateCustomer,
    deleteCustomer,
    addLocation,
    updateLocation,
    deleteLocation,
    addEquipment,
    updateEquipment,
    deleteEquipment,
    addServiceRecord,
    updateServiceRecord,
    softDeleteServiceRecord,
    restoreServiceRecord,
    permanentlyDeleteServiceRecord,
    deleteServicePhoto,
    addPartsListAttachments,
    deletePartsListPhoto,
    deletePartsListFile,
    addEquipmentWarranty,
    updateEquipmentWarranty,
    deleteEquipmentWarranty,
    updateEquipmentStatus,
    startTimeClock,
    stopTimeClock,
    addManualTimeEntry,
    updateTimeEntry,
    approveTimeEntry,
    unapproveTimeEntry,
    lockTimeEntry,
    unlockTimeEntry,
    deleteTimeEntry,
    addFacilityServiceTicket,
    updateFacilityServiceTicket,
    deleteFacilityServiceTicket,
    setOrgPayTypes,
    setOrgPayPeriodConfig,
    setOrgQuoteDefaults,
    setOrgMaintenanceDefaults,
    setOrgAddress,
    uploadOrgLogo,
    removeOrgLogo,
    addContact,
    updateContact,
    softDeleteContact,
    restoreContact,
    permanentlyDeleteContact,
    addJob,
    updateJob,
    finalizeBillWithInvoice,
    generateBill,
    acknowledgeInvoice,
    acceptRequestedJob,
    deleteJob,
    addServiceTicket,
    updateServiceTicket,
    deleteServiceTicket,
    deleteSignedTicket,
    moveTicket,
    addQuote,
    updateQuote,
    deleteQuote,
    amendQuote,
    approveQuoteAndCreateJob,
    approveQuoteAndCreateContract,
    addPurchaseOrder,
    addPurchaseOrderNumbered,
    updatePurchaseOrder,
    deletePurchaseOrder,
    markPOOrdered,
    addContract,
    updateContract,
    completeMaintenanceVisit,
    cancelContract,
    addVendor,
    updateVendor,
    deleteVendor,
    verifyVendorBill,
    voidBill,
    recordBillPayment,
    approveVendorBillForPayment,
    recordVendorBillPayment,
    rescheduleVisit,
    skipVisit,
    activateVisit,
    markNotificationRead,
    dismissNotifications,
    startMessageThread,
    sendMessage,
    markThreadRead,
    deleteMessageThread,
    appendToAuditLog,
    wipeOperationalData,
  };

  return {
    // Stitched customer tree — what the pages consume
    customers,

    // Raw flat lists — for code that queries directly
    customersRaw,
    locationsRaw,
    equipmentRaw,
    serviceRecordsRaw,
    contactsRaw,
    jobsRaw,
    serviceTicketsRaw,
    quotesRaw,
    purchaseOrdersRaw,
    contractsRaw,
    vendorsRaw,
    timeEntriesRaw,
    facilityServiceTicketsRaw,
    publicJobsRaw,
    publicTicketsRaw,
    publicInvoicesRaw,
    publicQuotesRaw,
    notificationsRaw,
    reconnectNotifications,
    messageThreadsRaw,
    reconnectMessageThreads,

    globalAuditLog,
    setGlobalAuditLog,
    dataLoading,
    errorMessage,
    setErrorMessage,

    // Write helpers
    actions,
  };
}
